import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiDownload, FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import { Bar, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FinCard, PageShell, formatBRL, FinSpinner, FinFieldTitle, type Lancamento, type CadastroItem, type Pagamento } from './financeiroShared';
import { FinDatePicker } from './FinDatePicker';
import { useFinanceiroData } from './FinanceiroDataContext';
import {
  downloadLancamentosExcelStyled,
  pickLancamentosParaExport,
  type ExportLancamentosSituacaoFiltro,
  type ExportLancamentosTipoFiltro,
} from './exportRecebimentosExcel';

export type { Lancamento } from './financeiroShared';

const LANC_PAGE_SIZE = 5;
const LEAVE_MS = 240;

type LancamentoFiltroSituacao =
  | 'todos'
  | 'pendentes'
  | 'vencidos'
  | 'vence_7'
  | 'sit_pendente'
  | 'sit_pago'
  | 'sit_atrasado';

/** Ícone calculadora (contorno) — mesmo estilo Feather, sem dependência de export específico em react-icons. */
function IconCalculator(props: React.SVGProps<SVGSVGElement>): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.15em"
      height="1.15em"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <rect width="16" height="20" x="4" y="2" rx="2" />
      <line x1="8" x2="16" y1="6" y2="6" />
      <path d="M8 12h.01M12 12h.01M16 12h.01M8 16h.01M12 16h.01M8 20h.01M12 20h.01M16 20h.01M16 16h.01" />
    </svg>
  );
}

function totalize(items: Lancamento[]) {
  const entradas = items.filter((l) => l.tipo === 'entrada');
  const saidas = items.filter((l) => l.tipo === 'saida');
  const totalEntradas = entradas.reduce((s, l) => s + l.valor, 0);
  const totalSaidas = saidas.reduce((s, l) => s + l.valor, 0);
  const recebido = entradas.reduce((s, l) => s + l.valorPago, 0);
  const aReceber = totalEntradas - recebido;
  const saldo = recebido - totalSaidas;
  return { totalEntradas, totalSaidas, recebido, aReceber, saldo };
}

/** Data local no formato YYYY-MM-DD (alinhado a lançamentos). */
function todayLocalIso(): string {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

function monthBounds(year: number, month: number): { start: string; end: string } {
  const start = `${year}-${String(month).padStart(2, '0')}-01`;
  const last = new Date(year, month, 0).getDate();
  const end = `${year}-${String(month).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { start, end };
}

function shiftMonth(year: number, month: number, deltaMonths: number): { year: number; month: number } {
  const d = new Date(year, month - 1 + deltaMonths, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

function addDaysToIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/** Opções de filtro: une listas entrada/saída sem duplicar o mesmo nome. */
function mergeCadastroOptionsForFilter(a: CadastroItem[], b: CadastroItem[]): CadastroItem[] {
  const byName = new Map<string, CadastroItem>();
  for (const c of [...a, ...b]) {
    if (!byName.has(c.name)) byName.set(c.name, c);
  }
  return Array.from(byName.values()).sort((x, y) => x.name.localeCompare(y.name, 'pt-BR'));
}

function situacaoBadgeLabel(s: Lancamento['situacao']): string {
  if (s === 'pago') return 'Pago';
  if (s === 'atrasado') return 'Em atraso';
  return 'Pendente';
}

function situacaoBadgeClass(s: Lancamento['situacao']): string {
  if (s === 'pago') return 'fin-tag fin-tag--sit-pago';
  if (s === 'atrasado') return 'fin-tag fin-tag--sit-atrasado';
  return 'fin-tag fin-tag--sit-pendente';
}

function filterByDataRange(items: Lancamento[], start: string, end: string): Lancamento[] {
  return items.filter((l) => l.data >= start && l.data <= end);
}

type LancSortKey =
  | 'data'
  | 'tipo'
  | 'descricao'
  | 'cliente'
  | 'categoria'
  | 'planoDeContas'
  | 'metodo'
  | 'vencimento'
  | 'dataPagamento'
  | 'valor'
  | 'valorPago'
  | 'situacao';

type SortDir = 'asc' | 'desc';
type LancSortState = { key: LancSortKey; dir: SortDir };

const LANC_SORT_DEFAULT_DIR: Record<LancSortKey, SortDir> = {
  data: 'desc',
  vencimento: 'desc',
  dataPagamento: 'desc',
  valor: 'desc',
  valorPago: 'desc',
  tipo: 'asc',
  descricao: 'asc',
  cliente: 'asc',
  categoria: 'asc',
  planoDeContas: 'asc',
  metodo: 'asc',
  situacao: 'asc',
};

function lancSortValue(l: Lancamento, key: LancSortKey): string | number {
  switch (key) {
    case 'data':
      return l.data;
    case 'vencimento':
      return l.vencimento ?? '';
    case 'dataPagamento':
      return l.dataPagamento ?? '';
    case 'tipo':
      return l.tipo;
    case 'descricao':
      return (l.descricao ?? '').toLowerCase();
    case 'cliente':
      return (l.cliente ?? '').toLowerCase();
    case 'categoria':
      return (l.categoria ?? '').toLowerCase();
    case 'planoDeContas':
      return (l.planoDeContas ?? '').toLowerCase();
    case 'metodo':
      return (l.metodo ?? '').toLowerCase();
    case 'situacao': {
      const order: Record<Lancamento['situacao'], number> = { atrasado: 0, pendente: 1, pago: 2 };
      return order[l.situacao] ?? 99;
    }
    case 'valor':
      return l.tipo === 'saida' ? -l.valor : l.valor;
    case 'valorPago':
      return l.tipo === 'saida' ? -l.valorPago : l.valorPago;
    default:
      return '';
  }
}

function compareLancamentos(a: Lancamento, b: Lancamento, sort: LancSortState): number {
  const va = lancSortValue(a, sort.key);
  const vb = lancSortValue(b, sort.key);
  let cmp: number;
  if (typeof va === 'number' && typeof vb === 'number') {
    cmp = va - vb;
  } else {
    cmp = String(va).localeCompare(String(vb), 'pt-BR');
  }
  if (cmp === 0) {
    if (a.data !== b.data) cmp = a.data.localeCompare(b.data);
    else cmp = a.id.localeCompare(b.id);
  }
  return sort.dir === 'asc' ? cmp : -cmp;
}

function useLancSort(initial: LancSortState): {
  sort: LancSortState;
  toggle: (k: LancSortKey) => void;
} {
  const [sort, setSort] = useState<LancSortState>(initial);
  const toggle = useCallback((k: LancSortKey) => {
    setSort((prev) =>
      prev.key === k
        ? { key: k, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: k, dir: LANC_SORT_DEFAULT_DIR[k] }
    );
  }, []);
  return { sort, toggle };
}

function SortHeader({
  sortKey,
  sort,
  onSort,
  className,
  align,
  children,
}: {
  sortKey: LancSortKey;
  sort: LancSortState;
  onSort: (k: LancSortKey) => void;
  className?: string;
  align?: 'left' | 'right';
  children: React.ReactNode;
}): React.ReactElement {
  const active = sort.key === sortKey;
  const aria: 'ascending' | 'descending' | 'none' = active
    ? sort.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  const arrow = active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅';
  const arrowEl = (
    <span
      className={`fin-th-sort__arrow${active ? ' fin-th-sort__arrow--active' : ''}`}
      aria-hidden
    >
      {arrow}
    </span>
  );
  const labelEl = <span className="fin-th-sort__label">{children}</span>;
  return (
    <th className={className} aria-sort={aria}>
      <button
        type="button"
        className={`fin-th-sort${active ? ' fin-th-sort--active' : ''}${align === 'right' ? ' fin-th-sort--right' : ''}`}
        onClick={() => onSort(sortKey)}
      >
        {align === 'right' ? (
          <>
            {arrowEl}
            {labelEl}
          </>
        ) : (
          <>
            {labelEl}
            {arrowEl}
          </>
        )}
      </button>
    </th>
  );
}

/**
 * Fluxo no período: o que entrou (valorPago em entradas) menos o que saiu de fato (valorPago em saídas).
 * Coerente com o hint “despesas pagas”.
 */
function totalizeCashflow(items: Lancamento[]) {
  const entradas = items.filter((l) => l.tipo === 'entrada');
  const saidas = items.filter((l) => l.tipo === 'saida');
  const totalEntradas = entradas.reduce((s, l) => s + l.valor, 0);
  const totalSaidasValor = saidas.reduce((s, l) => s + l.valor, 0);
  const pagoSaidas = saidas.reduce((s, l) => s + l.valorPago, 0);
  const recebido = entradas.reduce((s, l) => s + l.valorPago, 0);
  const aReceber = totalEntradas - recebido;
  const saldo = recebido - pagoSaidas;
  return { totalEntradas, totalSaidasValor, pagoSaidas, recebido, aReceber, saldo };
}

/**
 * Faturamento previsível: entradas em aberto agrupadas por janela do vencimento.
 * Vencimento ausente cai para o campo `data` (mesma regra usada em filtros existentes).
 */
function forecastEntradas(
  items: Lancamento[],
  refYear: number,
  refMonth: number
): { atrasado: number; proximoMes: number; proximos3Meses: number; semanaCorrente: number } {
  const today = todayLocalIso();
  const prox = shiftMonth(refYear, refMonth, 1);
  const proxTres = shiftMonth(refYear, refMonth, 3);
  const proxStart = monthBounds(prox.year, prox.month).start;
  const proxEnd = monthBounds(prox.year, prox.month).end;
  const proxTresEnd = monthBounds(proxTres.year, proxTres.month).end;
  const semanaEnd = addDaysToIso(today, 7);

  const aReceberOf = (l: Lancamento) => Math.max(0, l.valor - l.valorPago);
  const dueOf = (l: Lancamento) => l.vencimento ?? l.data;

  const abertos = items.filter((l) => l.tipo === 'entrada' && l.situacao !== 'pago');

  const atrasado = abertos.filter((l) => dueOf(l) < today).reduce((s, l) => s + aReceberOf(l), 0);
  const semanaCorrente = abertos
    .filter((l) => dueOf(l) >= today && dueOf(l) <= semanaEnd)
    .reduce((s, l) => s + aReceberOf(l), 0);
  const proximoMes = abertos
    .filter((l) => dueOf(l) >= proxStart && dueOf(l) <= proxEnd)
    .reduce((s, l) => s + aReceberOf(l), 0);
  const proximos3Meses = abertos
    .filter((l) => dueOf(l) >= proxStart && dueOf(l) <= proxTresEnd)
    .reduce((s, l) => s + aReceberOf(l), 0);

  return { atrasado, proximoMes, proximos3Meses, semanaCorrente };
}

const DASH_ENTRADAS_CAT_COLORS = [
  '#22c55e',
  '#14b8a6',
  '#0ea5e9',
  '#6366f1',
  '#8b5cf6',
  '#d946ef',
  '#f43f5e',
  '#f97316',
  '#eab308',
  '#84cc16',
];

function entradasPorCategoriaNoMes(items: Lancamento[]): { label: string; value: number }[] {
  const byCat = new Map<string, number>();
  for (const l of items) {
    if (l.tipo !== 'entrada') continue;
    const k = l.categoria?.trim() ? l.categoria.trim() : 'Sem categoria';
    byCat.set(k, (byCat.get(k) ?? 0) + l.valor);
  }
  return Array.from(byCat.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
}

export function FinanceiroDashboardPage(): React.ReactElement {
  const { lancamentos, lancamentosLoading, permissions } = useFinanceiroData();
  const navigate = useNavigate();

  const dash = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const { start: mesIni, end: mesFim } = monthBounds(y, m);
    const lMes = filterByDataRange(lancamentos, mesIni, mesFim);
    const tPrev = totalize(lMes);

    const months: { label: string; start: string; end: string }[] = [];
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(y, m - 1 - i, 1);
      const yy = d.getFullYear();
      const mm = d.getMonth() + 1;
      const { start, end } = monthBounds(yy, mm);
      const label = d
        .toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
        .replace('.', '');
      months.push({ label, start, end });
    }

    const series = months.map((b) => {
      const items = filterByDataRange(lancamentos, b.start, b.end);
      const totals = totalizeCashflow(items);
      return {
        label: b.label,
        previsto: totals.totalEntradas,
        recebido: totals.recebido,
        gastos: totals.pagoSaidas,
      };
    });

    const ultimos = [...lancamentos]
      .sort((a, b) => {
        if (a.data === b.data) return b.id.localeCompare(a.id);
        return b.data.localeCompare(a.data);
      })
      .slice(0, 8);

    const entradasPorCategoriaMes = entradasPorCategoriaNoMes(lMes);
    const forecast = forecastEntradas(lancamentos, y, m);

    return {
      tPrev,
      series,
      ultimos,
      entradasPorCategoriaMes,
      forecast,
      mesRef: { year: y, month: m },
    };
  }, [lancamentos]);

  const {
    tPrev,
    series,
    ultimos,
    entradasPorCategoriaMes,
    forecast,
    mesRef,
  } = dash;

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const { sort: dashSort, toggle: toggleDashSort } = useLancSort({ key: 'data', dir: 'desc' });
  const ultimosOrdenados = useMemo(
    () => [...ultimos].sort((a, b) => compareLancamentos(a, b, dashSort)),
    [ultimos, dashSort]
  );
  const temLancamentos = lancamentos.length > 0;
  const exportRangePadrao = useMemo(
    () => monthBounds(mesRef.year, mesRef.month),
    [mesRef.year, mesRef.month]
  );

  if (lancamentosLoading) {
    return (
      <PageShell title="Dashboard" subtitle="Carregando lançamentos…">
        <FinCard title="Aguarde">
          <p className="fin-card__hint">Buscando dados no servidor.</p>
        </FinCard>
      </PageShell>
    );
  }

  const saldoSign: 'pos' | 'neg' = tPrev.saldo >= 0 ? 'pos' : 'neg';

  const chartData = {
    labels: series.map((s) => s.label),
    datasets: [
      {
        label: 'Previsto',
        data: series.map((s) => s.previsto),
        borderColor: '#94a3b8',
        backgroundColor: 'rgba(148, 163, 184, 0.18)',
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 3,
        borderDash: [6, 4],
      },
      {
        label: 'Recebido',
        data: series.map((s) => s.recebido),
        borderColor: '#0C85BA',
        backgroundColor: 'rgba(12, 133, 186, 0.18)',
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 3,
      },
      {
        label: 'Gastos',
        data: series.map((s) => s.gastos),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.18)',
        tension: 0.3,
        borderWidth: 2,
        pointRadius: 3,
      },
    ],
  };

  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 12, boxHeight: 12, usePointStyle: true, pointStyle: 'circle' },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const raw = Number(ctx.parsed.y) || 0;
            const name = String(ctx.dataset?.label ?? '');
            const v = name === 'Gastos' ? -Math.abs(raw) : raw;
            return `${name}: ${formatBRL(v)}`;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (v: any) => formatBRL(Number(v) || 0),
        },
      },
    },
  };

  const barColors = entradasPorCategoriaMes.map(
    (_, i) => DASH_ENTRADAS_CAT_COLORS[i % DASH_ENTRADAS_CAT_COLORS.length]
  );
  const barChartData = {
    labels: entradasPorCategoriaMes.map((r) => r.label),
    datasets: [
      {
        label: 'Entradas no mês',
        data: entradasPorCategoriaMes.map((r) => r.value),
        backgroundColor: barColors.map((c) => `${c}66`),
        borderColor: barColors,
        borderWidth: 1.5,
        borderRadius: 8,
        borderSkipped: false,
        maxBarThickness: 36,
      },
    ],
  };

  const barChartHeightRem = Math.min(
    30,
    Math.max(14, 6 + entradasPorCategoriaMes.length * (entradasPorCategoriaMes.length > 8 ? 1.35 : 1.85))
  );

  const barChartOptions: any = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 8, left: 0, top: 4, bottom: 4 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        titleMarginBottom: 8,
        bodyFont: { size: 13 },
        titleFont: { size: 13, weight: '600' },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: (items: any[]) => (items[0] ? String(items[0].label) : ''),
          label: (ctx: any) => ` ${formatBRL(Number(ctx.parsed.x) || 0)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(148, 163, 184, 0.12)' },
        border: { display: false },
        ticks: {
          maxTicksLimit: 6,
          font: { size: 10 },
          callback: (v: any) => {
            const n = Number(v);
            if (!Number.isFinite(n)) return '';
            const abs = Math.abs(n);
            if (abs >= 1_000_000) return `R$ ${(n / 1_000_000).toFixed(1)}M`;
            if (abs >= 10_000) return `R$ ${(n / 1_000).toFixed(0)}k`;
            return formatBRL(n);
          },
        },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          autoSkip: false,
          font: { size: 11 },
          padding: 10,
          callback: (_v: string | number, i: number) => {
            const row = entradasPorCategoriaMes[i];
            if (!row) return '';
            const s = row.label;
            if (s.length <= 26) return s;
            return `${s.slice(0, 24)}…`;
          },
        },
      },
    },
  };

  return (
    <PageShell
      title="Dashboard"
      subtitle="Resumo do mês, evolução em linha e entradas por categoria."
      actions={
        <>
          <button
            type="button"
            className="fin-btn fin-btn--ghost fin-dash-export-btn"
            disabled={!temLancamentos}
            title={
              !temLancamentos
                ? 'Não há lançamentos para exportar.'
                : 'Exportar lançamentos em Excel (período e filtros no modal)'
            }
            onClick={() => setExportModalOpen(true)}
          >
            <FiDownload className="fin-dash-export-btn__icon" aria-hidden />
            Exportar Excel
          </button>
          <ExportRecebimentosModal
            open={exportModalOpen}
            onRequestClose={() => setExportModalOpen(false)}
            lancamentos={lancamentos}
            defaultStart={exportRangePadrao.start}
            defaultEnd={exportRangePadrao.end}
          />
        </>
      }
    >
      <FinCard title="Resumo do mês">
        <p className="fin-card__hint fin-dash-kpi-hint">
          Entradas e saídas no valor previsto do mês.
        </p>
        <ul className="fin-dash-kpi-strip">
          <li className="fin-dash-kpi">
            <span className="fin-dash-kpi__label">Entradas</span>
            <span className="fin-dash-kpi__value">{formatBRL(tPrev.totalEntradas)}</span>
          </li>
          <li className="fin-dash-kpi fin-dash-kpi--saidas">
            <span className="fin-dash-kpi__label">Saídas</span>
            <span className="fin-dash-kpi__value fin-dash-kpi__value--neg">{formatBRL(-tPrev.totalSaidas)}</span>
          </li>
          <li className="fin-dash-kpi">
            <span className="fin-dash-kpi__label">A receber</span>
            <span className="fin-dash-kpi__value">{formatBRL(tPrev.aReceber)}</span>
          </li>
          <li className="fin-dash-kpi">
            <span className="fin-dash-kpi__label">Saldo</span>
            <span className={`fin-dash-kpi__value fin-dash-kpi__value--${saldoSign}`}>{formatBRL(tPrev.saldo)}</span>
          </li>
        </ul>
      </FinCard>

      <FinCard title="Faturamento previsível">
        <p className="fin-card__hint fin-dash-kpi-hint">
          Entradas em aberto agrupadas pelo vencimento (ou data, se não houver vencimento). Considera apenas o saldo a receber.
        </p>
        <ul className="fin-dash-kpi-strip">
          <li className="fin-dash-kpi fin-dash-kpi--saidas">
            <span className="fin-dash-kpi__label">Atrasado a receber</span>
            <span className="fin-dash-kpi__value fin-dash-kpi__value--neg">{formatBRL(forecast.atrasado)}</span>
          </li>
          <li className="fin-dash-kpi">
            <span className="fin-dash-kpi__label">Próximos 7 dias</span>
            <span className="fin-dash-kpi__value">{formatBRL(forecast.semanaCorrente)}</span>
          </li>
          <li className="fin-dash-kpi">
            <span className="fin-dash-kpi__label">Próximo mês</span>
            <span className="fin-dash-kpi__value">{formatBRL(forecast.proximoMes)}</span>
          </li>
          <li className="fin-dash-kpi">
            <span className="fin-dash-kpi__label">Próximos 3 meses</span>
            <span className="fin-dash-kpi__value">{formatBRL(forecast.proximos3Meses)}</span>
          </li>
        </ul>
      </FinCard>

      <FinCard title="Evolução do fluxo e entradas por categoria">
        <div className="fin-dash-charts-grid">
          <div className="fin-dash-chart-panel fin-dash-chart-panel--line">
            <h3 className="fin-dash-chart-panel__title">Últimos 6 meses</h3>
            <div className="fin-dash-chart fin-dash-chart--line">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>
          <div className="fin-dash-chart-panel fin-dash-chart-panel--bars">
            <h3 className="fin-dash-chart-panel__title">Entradas por categoria (mês atual)</h3>
            {entradasPorCategoriaMes.length === 0 ? (
              <div className="fin-dash-bar-empty">
                <p>Não há entradas categorizadas neste mês para exibir.</p>
                <p className="fin-dash-bar-empty__hint">Cadastre categorias, planos de contas por tipo e classifique os lançamentos em Lançamentos.</p>
              </div>
            ) : (
              <>
                <div
                  className="fin-dash-chart fin-dash-chart--bar"
                  style={{ height: `${barChartHeightRem}rem` }}
                >
                  <Bar data={barChartData} options={barChartOptions} />
                </div>
                <div className="fin-dash-cat-legend" aria-label="Legenda por categoria">
                  <span className="fin-dash-cat-legend__heading">Legenda</span>
                  <ul className="fin-dash-cat-legend__list">
                    {entradasPorCategoriaMes.map((row, i) => (
                      <li key={`${row.label}-${i}`} className="fin-dash-cat-legend__item">
                        <span
                          className="fin-dash-cat-legend__swatch"
                          style={{ backgroundColor: barColors[i], borderColor: barColors[i] }}
                          aria-hidden
                        />
                        <span className="fin-dash-cat-legend__name" title={row.label}>
                          {row.label}
                        </span>
                        <span className="fin-dash-cat-legend__val">{formatBRL(row.value)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      </FinCard>

      <FinCard title={`Últimos lançamentos (${ultimos.length})`}>
        <div className="fin-table-wrap fin-table-wrap--lanc">
          <table className="fin-table fin-table--lanc">
            <thead>
              <tr>
                <SortHeader sortKey="data" sort={dashSort} onSort={toggleDashSort}>Data</SortHeader>
                <SortHeader sortKey="tipo" sort={dashSort} onSort={toggleDashSort} className="fin-lanc-col-tipo">Tipo</SortHeader>
                <SortHeader sortKey="descricao" sort={dashSort} onSort={toggleDashSort}>Descrição</SortHeader>
                <SortHeader sortKey="cliente" sort={dashSort} onSort={toggleDashSort}>Cliente</SortHeader>
                <SortHeader sortKey="categoria" sort={dashSort} onSort={toggleDashSort} className="fin-lanc-col-cat">
                  Categoria
                </SortHeader>
                <SortHeader sortKey="planoDeContas" sort={dashSort} onSort={toggleDashSort} className="fin-lanc-col-cat">
                  <span className="fin-lanc-th-stacked">
                    Plano de
                    <br />
                    contas
                  </span>
                </SortHeader>
                <SortHeader sortKey="dataPagamento" sort={dashSort} onSort={toggleDashSort}>
                  <span className="fin-lanc-th-stacked">
                    Data
                    <br />
                    pago
                  </span>
                </SortHeader>
                <SortHeader sortKey="situacao" sort={dashSort} onSort={toggleDashSort}>Situação</SortHeader>
                <SortHeader sortKey="valor" sort={dashSort} onSort={toggleDashSort}>Valor</SortHeader>
                <SortHeader sortKey="valorPago" sort={dashSort} onSort={toggleDashSort}>Pago</SortHeader>
                <th className="fin-table__th-actions" scope="col">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              {ultimosOrdenados.length === 0 ? (
                <tr>
                  <td colSpan={11} className="fin-table__empty">
                    Nenhum lançamento ainda.
                  </td>
                </tr>
              ) : (
                ultimosOrdenados.map((l) => (
                  <tr key={l.id}>
                    <td>
                      {new Date(l.data + 'T12:00:00').toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                      })}
                    </td>
                    <td className="fin-lanc-col-tipo">
                      <span className={`fin-tag fin-tag--${l.tipo === 'entrada' ? 'pos' : 'neg'}`}>
                        {l.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                      </span>
                    </td>
                    <td className="fin-lanc-cell-desc">
                      <span className="fin-lanc-cell-desc__main">{l.descricao}</span>
                      {l.anexoUrl ? (
                        <a
                          href={l.anexoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fin-lanc-comprovante-link"
                        >
                          Visualizar comprovante
                        </a>
                      ) : null}
                    </td>
                    <td className="fin-lanc-cell-who">
                      <span>{l.cliente ?? '—'}</span>
                    </td>
                    <td className="fin-lanc-col-cat">{l.categoria ?? '—'}</td>
                    <td className="fin-lanc-col-cat">{l.planoDeContas ?? '—'}</td>
                    <td>
                      {l.dataPagamento ? new Date(l.dataPagamento + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'}
                    </td>
                    <td className="fin-lanc-col-situacao">
                      <span className={situacaoBadgeClass(l.situacao)}>{situacaoBadgeLabel(l.situacao)}</span>
                    </td>
                    <td className={`fin-table__num${l.tipo === 'saida' ? ' fin-money--out' : ''}`}>
                      {formatBRL(l.tipo === 'saida' ? -l.valor : l.valor)}
                    </td>
                    <td className={`fin-table__num${l.tipo === 'saida' ? ' fin-money--out' : ''}`}>
                      {formatBRL(l.tipo === 'saida' ? -l.valorPago : l.valorPago)}
                      {l.valorPago < l.valor ? (
                        <div
                          className={`fin-table__hint${l.tipo === 'saida' ? ' fin-table__hint--out' : ''}`}
                        >
                          {l.tipo === 'saida'
                            ? formatBRL(-(l.valor - l.valorPago))
                            : formatBRL(l.valor - l.valorPago)}
                        </div>
                      ) : null}
                    </td>
                    <td className="fin-table__actions">
                      <div className="fin-row-actions">
                        <button
                          type="button"
                          className="fin-icon-btn fin-icon-btn--ghost"
                          onClick={() =>
                            navigate(`/financeiro/lancamentos?consultar=${encodeURIComponent(l.id)}`)
                          }
                          aria-label={`Consultar — ${l.descricao}`}
                          title="Consultar"
                        >
                          <FiEye aria-hidden />
                        </button>
                        {permissions.canEdit ? (
                          <button
                            type="button"
                            className="fin-icon-btn fin-icon-btn--ghost"
                            onClick={() =>
                              navigate(`/financeiro/lancamentos?edit=${encodeURIComponent(l.id)}`)
                            }
                            aria-label={`Editar — ${l.descricao}`}
                            title="Editar"
                          >
                            <FiEdit2 aria-hidden />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </FinCard>
    </PageShell>
  );
}

export function FinanceiroLancamentosPage(): React.ReactElement {
  const {
    lancamentos,
    lancamentosLoading,
    lancamentoSaving,
    createLancamento,
    createLancamentosBatch,
    updateLancamento,
    deleteLancamento,
    permissions,
    clientes,
    categorias,
    planosDeContasEntrada,
    planosDeContasSaida,
    metodos,
  } = useFinanceiroData();

  const categoriasFiltroOpts = categorias;
  const planosFiltroOpts = useMemo(
    () => mergeCadastroOptionsForFilter(planosDeContasEntrada, planosDeContasSaida),
    [planosDeContasEntrada, planosDeContasSaida]
  );
  const [filtro, setFiltro] = useState<'todos' | 'entrada' | 'saida'>('todos');
  const [busca, setBusca] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroPlanoDeContas, setFiltroPlanoDeContas] = useState('');
  const [filtroMetodo, setFiltroMetodo] = useState('');
  const [filtroSituacao, setFiltroSituacao] = useState<LancamentoFiltroSituacao>('todos');
  const [openForm, setOpenForm] = useState(false);
  const [editLancamento, setEditLancamento] = useState<Lancamento | null>(null);
  const [consultarLancamento, setConsultarLancamento] = useState<Lancamento | null>(null);
  const [lancPagina, setLancPagina] = useState(1);
  const [lancDeleteConfirm, setLancDeleteConfirm] = useState<Lancamento | null>(null);
  const [alocarPagamento, setAlocarPagamento] = useState<Lancamento | null>(null);
  const [expandedLancIds, setExpandedLancIds] = useState<Set<string>>(() => new Set());
  const toggleLancExpanded = useCallback((id: string) => {
    setExpandedLancIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!permissions.canCreate && openForm) setOpenForm(false);
  }, [permissions.canCreate, openForm]);

  useEffect(() => {
    const editId = searchParams.get('edit');
    const consultarId = searchParams.get('consultar');
    if (!editId && !consultarId) return;
    if (lancamentosLoading) return;
    if (editId) {
      const found = lancamentos.find((l) => l.id === editId);
      if (found && permissions.canEdit) {
        setConsultarLancamento(null);
        setOpenForm(false);
        setEditLancamento(found);
      }
    } else if (consultarId) {
      const found = lancamentos.find((l) => l.id === consultarId);
      if (found) {
        setEditLancamento(null);
        setOpenForm(false);
        setConsultarLancamento(found);
      }
    }
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    next.delete('consultar');
    setSearchParams(next, { replace: true });
  }, [searchParams, lancamentos, lancamentosLoading, permissions.canEdit, setSearchParams]);

  const visiveis = useMemo(() => {
    const today = todayLocalIso();
    const limite7 = addDaysToIso(today, 7);
    const haystack = (l: Lancamento) =>
      `${l.descricao} ${l.cliente ?? ''} ${l.categoria ?? ''} ${l.planoDeContas ?? ''} ${l.metodo ?? ''} ${l.responsavel ?? ''}`.toLowerCase();
    return lancamentos.filter((l) => {
      if (filtro !== 'todos' && l.tipo !== filtro) return false;
      if (busca.trim() !== '' && !haystack(l).includes(busca.trim().toLowerCase())) return false;
      if (filtroCliente && l.cliente !== filtroCliente) return false;
      if (filtroCategoria && l.categoria !== filtroCategoria) return false;
      if (filtroPlanoDeContas && l.planoDeContas !== filtroPlanoDeContas) return false;
      if (filtroMetodo && l.metodo !== filtroMetodo) return false;
      const emAberto = l.valorPago < l.valor - 1e-9;
      if (filtroSituacao === 'sit_pendente' && l.situacao !== 'pendente') return false;
      if (filtroSituacao === 'sit_pago' && l.situacao !== 'pago') return false;
      if (filtroSituacao === 'sit_atrasado' && l.situacao !== 'atrasado') return false;
      if (filtroSituacao === 'pendentes' && !emAberto) return false;
      if (filtroSituacao === 'vencidos') {
        if (l.situacao !== 'atrasado') return false;
      }
      if (filtroSituacao === 'vence_7') {
        if (!emAberto) return false;
        if (!l.vencimento || l.vencimento < today || l.vencimento > limite7) return false;
      }
      return true;
    });
  }, [
    lancamentos,
    filtro,
    busca,
    filtroCliente,
    filtroCategoria,
    filtroPlanoDeContas,
    filtroMetodo,
    filtroSituacao,
  ]);

  const { sort: lancSort, toggle: toggleLancSort } = useLancSort({ key: 'data', dir: 'desc' });

  const visiveisOrdenados = useMemo(
    () => [...visiveis].sort((a, b) => compareLancamentos(a, b, lancSort)),
    [visiveis, lancSort]
  );

  const totalPaginasLanc = Math.max(1, Math.ceil(visiveisOrdenados.length / LANC_PAGE_SIZE));

  useEffect(() => {
    setLancPagina((p) => Math.min(Math.max(1, p), totalPaginasLanc));
  }, [totalPaginasLanc]);

  useEffect(() => {
    setLancPagina(1);
  }, [lancSort]);

  const visiveisPaginados = useMemo(() => {
    const off = (lancPagina - 1) * LANC_PAGE_SIZE;
    return visiveisOrdenados.slice(off, off + LANC_PAGE_SIZE);
  }, [visiveisOrdenados, lancPagina]);

  const t = totalize(visiveis);

  const filtrosExtrasAtivos =
    Boolean(filtroCliente) ||
    Boolean(filtroCategoria) ||
    Boolean(filtroPlanoDeContas) ||
    Boolean(filtroMetodo) ||
    filtroSituacao !== 'todos';

  const limparFiltrosExtras = () => {
    setFiltroCliente('');
    setFiltroCategoria('');
    setFiltroPlanoDeContas('');
    setFiltroMetodo('');
    setFiltroSituacao('todos');
  };

  if (lancamentosLoading) {
    return (
      <PageShell title="Lançamentos" subtitle="Carregando…">
        <FinCard title="Aguarde">
          <p className="fin-card__hint">Buscando lançamentos no servidor.</p>
        </FinCard>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Lançamentos"
      subtitle="Entradas, saídas, parcelas e comprovantes numa única lista."
      actions={
        permissions.canCreate && !openForm && !editLancamento && !consultarLancamento ? (
          <button
            className="fin-btn fin-btn--primary"
            type="button"
            onClick={() => {
              setEditLancamento(null);
              setConsultarLancamento(null);
              setOpenForm(true);
            }}
          >
            Novo lançamento
          </button>
        ) : null
      }
    >
      {openForm && permissions.canCreate ? (
        <FinCard
          title="Novo lançamento"
          className="fin-card--popover-safe fin-card--lanc-form"
          headerRight={
            <button
              type="button"
              className="fin-btn fin-btn--ghost fin-btn--small"
              onClick={() => {
                setConsultarLancamento(null);
                setOpenForm(false);
              }}
              disabled={lancamentoSaving}
            >
              Fechar
            </button>
          }
        >
          <NovoLancamentoForm
            saving={lancamentoSaving}
            onSalvar={async (l) => {
              const ok = await createLancamento(l);
              if (ok) setOpenForm(false);
            }}
            onSalvarLote={async (items) => {
              const ok = await createLancamentosBatch(items);
              if (ok) setOpenForm(false);
            }}
            onCancelar={() => {
              setConsultarLancamento(null);
              setOpenForm(false);
            }}
          />
        </FinCard>
      ) : null}

      {editLancamento && permissions.canEdit ? (
        <FinCard
          title="Editar lançamento"
          className="fin-card--popover-safe fin-card--lanc-form"
          headerRight={
            <button
              type="button"
              className="fin-btn fin-btn--ghost fin-btn--small"
              onClick={() => {
                if (!lancamentoSaving) {
                  setConsultarLancamento(null);
                  setEditLancamento(null);
                }
              }}
              disabled={lancamentoSaving}
            >
              Fechar
            </button>
          }
        >
          <NovoLancamentoForm
            saving={lancamentoSaving}
            editing={editLancamento}
            onSalvarEdicao={async (id, payload) => {
              const ok = await updateLancamento(id, payload);
              if (ok) setEditLancamento(null);
            }}
            onSalvar={async () => {}}
            onSalvarLote={async () => {}}
            onCancelar={() => {
              setConsultarLancamento(null);
              setEditLancamento(null);
            }}
          />
        </FinCard>
      ) : null}

      {consultarLancamento ? (
        <FinCard
          title="Consultar lançamento"
          className="fin-card--popover-safe fin-card--lanc-form"
          headerRight={
            <button
              type="button"
              className="fin-btn fin-btn--ghost fin-btn--small"
              onClick={() => setConsultarLancamento(null)}
              disabled={lancamentoSaving}
            >
              Fechar
            </button>
          }
        >
          <NovoLancamentoForm
            readOnly
            saving={false}
            editing={consultarLancamento}
            onSalvar={async () => {}}
            onSalvarLote={async () => {}}
            onCancelar={() => setConsultarLancamento(null)}
          />
        </FinCard>
      ) : null}

      <FinConfirmModal
        open={lancDeleteConfirm != null}
        title="Excluir lançamento?"
        message={
          lancDeleteConfirm ? (
            <>
              Tem certeza que deseja excluir este lançamento? Esta ação não pode ser desfeita.
            </>
          ) : null
        }
        busy={lancamentoSaving}
        onCancel={() => {
          if (!lancamentoSaving) setLancDeleteConfirm(null);
        }}
        onConfirm={async () => {
          if (!lancDeleteConfirm) return;
          const ok = await deleteLancamento(lancDeleteConfirm.id);
          if (ok) setLancDeleteConfirm(null);
        }}
      />

      <LancamentoAlocarPagamentoModal
        anchor={alocarPagamento}
        allLancamentos={lancamentos}
        metodos={metodos}
        busy={lancamentoSaving}
        onClose={() => setAlocarPagamento(null)}
        updateLancamento={updateLancamento}
      />

      {!(
        (openForm && permissions.canCreate) ||
        (editLancamento && permissions.canEdit) ||
        consultarLancamento
      ) ? (
        <>
          <FinCard title="Filtros e resumo">
        <div className="fin-filter-resumo">
          <div className="fin-filter-resumo__top">
            <div className="fin-segmented" role="tablist" aria-label="Tipo de lançamento">
              {(['todos', 'entrada', 'saida'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={filtro === f}
                  className={`fin-segmented__btn${filtro === f ? ' fin-segmented__btn--active' : ''}`}
                  onClick={() => setFiltro(f)}
                >
                  {f === 'todos' ? 'Todos' : f === 'entrada' ? 'Entradas' : 'Saídas'}
                </button>
              ))}
            </div>
            <label className="fin-filter-search">
              <span className="fin-filter-search__icon" aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </span>
              <input
                className="fin-filter-search__input"
                placeholder="Buscar descrição, cliente, categoria, plano de contas, método, responsável…"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                aria-label="Buscar lançamentos"
              />
            </label>
          </div>
          <div className="fin-filter-resumo__filters" role="group" aria-label="Filtros por cadastro e situação">
            <div className="fin-filter-field">
              <FinFieldTitle htmlFor="fin-lanc-fil-cliente">Cliente</FinFieldTitle>
              <select
                id="fin-lanc-fil-cliente"
                className="fin-ctrl fin-ctrl--compact"
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
              >
                <option value="">Todos</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fin-filter-field">
              <FinFieldTitle htmlFor="fin-lanc-fil-cat">Categoria</FinFieldTitle>
              <select
                id="fin-lanc-fil-cat"
                className="fin-ctrl fin-ctrl--compact"
                value={filtroCategoria}
                onChange={(e) => setFiltroCategoria(e.target.value)}
              >
                <option value="">Todas</option>
                {categoriasFiltroOpts.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fin-filter-field">
              <FinFieldTitle htmlFor="fin-lanc-fil-plano">Plano de contas</FinFieldTitle>
              <select
                id="fin-lanc-fil-plano"
                className="fin-ctrl fin-ctrl--compact"
                value={filtroPlanoDeContas}
                onChange={(e) => setFiltroPlanoDeContas(e.target.value)}
              >
                <option value="">Todos</option>
                {planosFiltroOpts.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fin-filter-field">
              <FinFieldTitle htmlFor="fin-lanc-fil-met">Método de pagamento</FinFieldTitle>
              <select
                id="fin-lanc-fil-met"
                className="fin-ctrl fin-ctrl--compact"
                value={filtroMetodo}
                onChange={(e) => setFiltroMetodo(e.target.value)}
              >
                <option value="">Todos</option>
                {metodos.map((m) => (
                  <option key={m.id} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fin-filter-field">
              <FinFieldTitle htmlFor="fin-lanc-fil-sit">Situação</FinFieldTitle>
              <select
                id="fin-lanc-fil-sit"
                className="fin-ctrl fin-ctrl--compact"
                value={filtroSituacao}
                onChange={(e) => setFiltroSituacao(e.target.value as LancamentoFiltroSituacao)}
              >
                <option value="todos">Todos</option>
                <option value="sit_pendente">Pendente</option>
                <option value="sit_pago">Pago</option>
                <option value="sit_atrasado">Em atraso</option>
                <option value="pendentes">Com saldo em aberto</option>
                <option value="vencidos">Vencidos (em atraso)</option>
                <option value="vence_7">Vence em até 7 dias</option>
              </select>
            </div>
            {filtrosExtrasAtivos ? (
              <div className="fin-filter-resumo__filters-clear">
                <button type="button" className="fin-btn fin-btn--ghost fin-btn--small" onClick={limparFiltrosExtras}>
                  Limpar filtros
                </button>
              </div>
            ) : null}
          </div>
          <div className="fin-resumo-grid" aria-label="Totais do filtro atual">
            <div className="fin-resumo-tile fin-resumo-tile--in">
              <span className="fin-resumo-tile__label">Entradas</span>
              <span className="fin-resumo-tile__value">{formatBRL(t.totalEntradas)}</span>
            </div>
            <div className="fin-resumo-tile fin-resumo-tile--out">
              <span className="fin-resumo-tile__label">Saídas</span>
              <span className="fin-resumo-tile__value fin-resumo-tile__value--saidas">{formatBRL(-t.totalSaidas)}</span>
            </div>
            <div className="fin-resumo-tile fin-resumo-tile--pending">
              <span className="fin-resumo-tile__label">A receber</span>
              <span className="fin-resumo-tile__value">{formatBRL(t.aReceber)}</span>
            </div>
            <div
              className={`fin-resumo-tile fin-resumo-tile--balance${t.saldo >= 0 ? ' fin-resumo-tile--balance-pos' : ' fin-resumo-tile--balance-neg'}`}
            >
              <span className="fin-resumo-tile__label">Saldo</span>
              <span className="fin-resumo-tile__value">{formatBRL(t.saldo)}</span>
            </div>
          </div>
        </div>
      </FinCard>

      <FinCard title={`Lançamentos (${visiveis.length})`}>
        <div className="fin-table-wrap fin-table-wrap--lanc">
        <table className="fin-table fin-table--lanc">
          <thead>
            <tr>
              <SortHeader sortKey="data" sort={lancSort} onSort={toggleLancSort}>Data</SortHeader>
              <SortHeader sortKey="tipo" sort={lancSort} onSort={toggleLancSort} className="fin-lanc-col-tipo">Tipo</SortHeader>
              <SortHeader sortKey="descricao" sort={lancSort} onSort={toggleLancSort}>Descrição</SortHeader>
              <SortHeader sortKey="cliente" sort={lancSort} onSort={toggleLancSort}>Cliente</SortHeader>
              <SortHeader sortKey="categoria" sort={lancSort} onSort={toggleLancSort} className="fin-lanc-col-cat">
                Categoria
              </SortHeader>
              <SortHeader sortKey="planoDeContas" sort={lancSort} onSort={toggleLancSort} className="fin-lanc-col-cat">
                <span className="fin-lanc-th-stacked">
                  Plano de
                  <br />
                  contas
                </span>
              </SortHeader>
              <SortHeader sortKey="metodo" sort={lancSort} onSort={toggleLancSort} className="fin-lanc-col-met">
                <span className="fin-lanc-th-stacked">
                  Método de
                  <br />
                  pagamento
                </span>
              </SortHeader>
              <th>Parc.</th>
              <SortHeader sortKey="vencimento" sort={lancSort} onSort={toggleLancSort} className="fin-lanc-col-venc">Venc.</SortHeader>
              <SortHeader sortKey="dataPagamento" sort={lancSort} onSort={toggleLancSort} className="fin-lanc-col-venc">
                <span className="fin-lanc-th-stacked">
                  Data
                  <br />
                  pago
                </span>
              </SortHeader>
              <SortHeader sortKey="valor" sort={lancSort} onSort={toggleLancSort}>Valor</SortHeader>
              <SortHeader sortKey="valorPago" sort={lancSort} onSort={toggleLancSort}>Pago</SortHeader>
              <SortHeader sortKey="situacao" sort={lancSort} onSort={toggleLancSort} className="fin-lanc-col-situacao">Situação</SortHeader>
              <th className="fin-table__th-actions" scope="col">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {visiveis.length === 0 ? (
              <tr>
                <td colSpan={14} className="fin-table__empty">
                  Nenhum lançamento.
                </td>
              </tr>
            ) : (
              visiveisPaginados.map((l) => {
                const grp = getParcelGroup(l, lancamentos);
                const showCalc = permissions.canEdit && parcelGroupHasOpenBalance(grp);
                const pagamentos = l.pagamentos ?? [];
                // Só faz sentido expandir quando há 2+ pagamentos (parcial real);
                // pagamento integral em quitação única já está resumido no próprio pai.
                const hasPartials = pagamentos.length >= 2;
                const isExpanded = hasPartials && expandedLancIds.has(l.id);
                const sortedPagamentos = hasPartials
                  ? [...pagamentos].sort((a, b) => (a.data === b.data ? 0 : a.data < b.data ? -1 : 1))
                  : [];
                return (
                <React.Fragment key={l.id}>
                <tr className={isExpanded ? 'fin-lanc-row--has-children' : undefined}>
                  <td>
                    <div className="fin-lanc-cell-data">
                      {hasPartials ? (
                        <button
                          type="button"
                          className={`fin-lanc-expand-btn${isExpanded ? ' fin-lanc-expand-btn--open' : ''}`}
                          onClick={() => toggleLancExpanded(l.id)}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? `Recolher pagamentos de ${l.descricao}` : `Expandir pagamentos de ${l.descricao}`}
                          title={isExpanded ? 'Recolher pagamentos' : `Mostrar ${pagamentos.length} ${pagamentos.length === 1 ? 'pagamento' : 'pagamentos'}`}
                        >
                          {isExpanded ? <FiChevronDown aria-hidden /> : <FiChevronRight aria-hidden />}
                        </button>
                      ) : (
                        <span className="fin-lanc-expand-spacer" aria-hidden />
                      )}
                      <span>{new Date(l.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                    </div>
                  </td>
                  <td className="fin-lanc-col-tipo">
                    <span className={`fin-tag fin-tag--${l.tipo === 'entrada' ? 'pos' : 'neg'}`}>
                      {l.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                    </span>
                  </td>
                  <td className="fin-lanc-cell-desc">
                    <span className="fin-lanc-cell-desc__main">{l.descricao}</span>
                    {l.anexo ? <span className="fin-table__hint fin-lanc-cell-desc__hint">{l.anexo}</span> : null}
                    {l.anexoUrl ? (
                      <a
                        href={l.anexoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fin-lanc-comprovante-link"
                      >
                        Visualizar comprovante
                      </a>
                    ) : null}
                  </td>
                  <td className="fin-lanc-cell-who">
                    <span>{l.cliente ?? '—'}</span>
                    {l.responsavel ? <span className="fin-table__hint">{l.responsavel}</span> : null}
                  </td>
                  <td className="fin-lanc-col-cat">{l.categoria ?? '—'}</td>
                  <td className="fin-lanc-col-cat">{l.planoDeContas ?? '—'}</td>
                  <td className="fin-lanc-col-met">{l.metodo ?? '—'}</td>
                  <td>{l.parcelaAtual ? `${l.parcelaAtual}/${l.totalParcelas}` : '—'}</td>
                  <td className="fin-lanc-col-venc">
                    {l.vencimento ? new Date(l.vencimento + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'}
                  </td>
                  <td className="fin-lanc-col-venc">
                    {l.dataPagamento ? new Date(l.dataPagamento + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—'}
                  </td>
                  <td className={`fin-table__num${l.tipo === 'saida' ? ' fin-money--out' : ''}`}>
                    {formatBRL(l.tipo === 'saida' ? -l.valor : l.valor)}
                  </td>
                  <td className={`fin-table__num${l.tipo === 'saida' ? ' fin-money--out' : ''}`}>
                    {formatBRL(l.tipo === 'saida' ? -l.valorPago : l.valorPago)}
                    {l.valorPago < l.valor ? (
                      <div
                        className={`fin-table__hint${l.tipo === 'saida' ? ' fin-table__hint--out' : ''}`}
                      >
                        {l.tipo === 'saida'
                          ? formatBRL(-roundMoney2(l.valor - l.valorPago))
                          : formatBRL(roundMoney2(l.valor - l.valorPago))}
                      </div>
                    ) : null}
                  </td>
                  <td className="fin-lanc-col-situacao">
                    <span className={situacaoBadgeClass(l.situacao)}>{situacaoBadgeLabel(l.situacao)}</span>
                  </td>
                  <td className="fin-table__actions">
                    <div className="fin-row-actions">
                      <button
                        type="button"
                        className="fin-icon-btn fin-icon-btn--ghost"
                        disabled={lancamentoSaving}
                        onClick={() => {
                          setOpenForm(false);
                          setEditLancamento(null);
                          setConsultarLancamento(l);
                        }}
                        aria-label={`Consultar lançamento ${l.descricao}`}
                        title="Consultar"
                      >
                        <FiEye aria-hidden />
                      </button>
                      {showCalc ? (
                        <button
                          type="button"
                          className="fin-icon-btn fin-icon-btn--ghost"
                          disabled={lancamentoSaving}
                          onClick={() => setAlocarPagamento(l)}
                          aria-label={`Registrar pagamento — ${l.descricao}`}
                          title="Registrar pagamento (distribui entre parcelas)"
                        >
                          <IconCalculator />
                        </button>
                      ) : null}
                      {permissions.canEdit ? (
                        <button
                          type="button"
                          className="fin-icon-btn fin-icon-btn--ghost"
                          disabled={lancamentoSaving}
                          onClick={() => {
                            setOpenForm(false);
                            setConsultarLancamento(null);
                            setEditLancamento(l);
                          }}
                          aria-label={`Editar lançamento ${l.descricao}`}
                        >
                          <FiEdit2 aria-hidden />
                        </button>
                      ) : null}
                      {permissions.canDelete ? (
                        <button
                          type="button"
                          className="fin-icon-btn fin-icon-btn--danger"
                          disabled={lancamentoSaving}
                          onClick={() => setLancDeleteConfirm(l)}
                          aria-label={`Excluir lançamento ${l.descricao}`}
                        >
                          <FiTrash2 aria-hidden />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
                {isExpanded
                  ? sortedPagamentos.map((p, idx) => {
                      const valorAssinado = l.tipo === 'saida' ? -p.valor : p.valor;
                      const isFullyPaid = l.situacao === 'pago';
                      const tagLabel = isFullyPaid ? 'Pagamento' : 'Parcial';
                      const descLabel = isFullyPaid
                        ? l.tipo === 'entrada'
                          ? 'Recebimento'
                          : 'Pagamento'
                        : l.tipo === 'entrada'
                          ? 'Recebimento parcial'
                          : 'Pagamento parcial';
                      return (
                        <tr key={`${l.id}-pag-${p.id ?? idx}`} className="fin-lanc-row--child">
                          <td className="fin-lanc-child-cell">
                            <div className="fin-lanc-cell-data fin-lanc-cell-data--child">
                              <span className="fin-lanc-child-branch" aria-hidden>↳</span>
                              <span>{new Date(p.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                            </div>
                          </td>
                          <td className="fin-lanc-col-tipo">
                            <span className="fin-tag fin-tag--muted">{tagLabel}</span>
                          </td>
                          <td className="fin-lanc-cell-desc">
                            <span className="fin-lanc-cell-desc__main fin-lanc-child-desc">{descLabel}</span>
                          </td>
                          <td className="fin-lanc-cell-who">
                            <span>{p.responsavel ?? '—'}</span>
                          </td>
                          <td className="fin-lanc-col-cat">—</td>
                          <td className="fin-lanc-col-cat">—</td>
                          <td className="fin-lanc-col-met">{p.metodo?.trim() || l.metodo || '—'}</td>
                          <td>—</td>
                          <td className="fin-lanc-col-venc">—</td>
                          <td className="fin-lanc-col-venc">
                            {new Date(p.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                          </td>
                          <td className="fin-table__num">—</td>
                          <td className={`fin-table__num${l.tipo === 'saida' ? ' fin-money--out' : ''}`}>
                            {formatBRL(valorAssinado)}
                          </td>
                          <td className="fin-lanc-col-situacao">
                            <span className="fin-tag fin-tag--muted">{idx + 1}/{sortedPagamentos.length}</span>
                          </td>
                          <td className="fin-table__actions" aria-hidden></td>
                        </tr>
                      );
                    })
                  : null}
                </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
        </div>
        {visiveis.length > LANC_PAGE_SIZE ? (
          <div className="fin-lanc-pager" role="navigation" aria-label="Paginação de lançamentos">
            <span className="fin-lanc-pager__meta">
              Página <span className="fin-lanc-pager__meta-num">{lancPagina}</span>{' '}
              <span className="fin-lanc-pager__meta-mid">de</span>{' '}
              <span className="fin-lanc-pager__meta-num">{totalPaginasLanc}</span>
            </span>
            <div className="fin-lanc-pager__btns">
              <button
                type="button"
                className="fin-btn fin-btn--ghost fin-btn--small"
                disabled={lancPagina <= 1}
                onClick={() => setLancPagina((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                className="fin-btn fin-btn--ghost fin-btn--small"
                disabled={lancPagina >= totalPaginasLanc}
                onClick={() => setLancPagina((p) => Math.min(totalPaginasLanc, p + 1))}
              >
                Próxima
              </button>
            </div>
          </div>
        ) : null}
      </FinCard>
        </>
      ) : null}
    </PageShell>
  );
}

function parseMoneyInput(raw: string): number | null {
  const s = raw.replace(/\s/g, '');
  if (s === '') return null;
  const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function decimalToBrInput(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function newBatchLineId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `fin-bl-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

const BATCH_ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isValidBatchIsoDate(s: string): boolean {
  const t = s.trim();
  if (!BATCH_ISO_DATE_RE.test(t)) return false;
  const [y, m, d] = t.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/** +1 mês civil (ajusta fim do mês e ano). Entrada yyyy-mm-dd. */
function addOneMonthIso(iso: string): string {
  const t = iso.trim();
  if (!isValidBatchIsoDate(t)) return '';
  const [y, m, d] = t.split('-').map(Number);
  const dt = new Date(y, m - 1 + 1, d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/** Competência enviada à API (`data`): vencimento válido se houver; senão hoje (local). */
function resolveFinanceiroDataCompetencia(vencimento: string): string {
  const v = vencimento.trim();
  if (v && isValidBatchIsoDate(v)) return v;
  return todayLocalIso();
}

function readFileAsBase64Part(file: File): Promise<{ base64: string; contentType: string }> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const res = String(r.result ?? '');
      const i = res.indexOf(',');
      resolve({
        base64: i >= 0 ? res.slice(i + 1) : res,
        contentType: file.type || 'application/octet-stream',
      });
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function roundMoney2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function isMultiParcel(l: Lancamento): boolean {
  return (l.totalParcelas ?? 0) > 1;
}

function parcelDescricaoStemForGroup(d: string): string {
  const t = d.trim();
  const m = t.match(/^(.*?)(\s*—\s*Parcela\s+\d+\/\d+)\s*$/i);
  return (m ? m[1] : t).trim();
}

function parcelGroupKey(l: Lancamento): string | null {
  if (!isMultiParcel(l)) return null;
  const stem = parcelDescricaoStemForGroup(l.descricao);
  return [
    l.tipo,
    stem,
    l.cliente ?? '',
    l.categoria ?? '',
    l.planoDeContas ?? '',
    l.metodo ?? '',
    String(l.totalParcelas ?? 0),
  ].join('\u0001');
}

function getParcelGroup(l: Lancamento, pool: Lancamento[]): Lancamento[] {
  const key = parcelGroupKey(l);
  if (key == null) return [l];
  const g = pool.filter((x) => parcelGroupKey(x) === key);
  g.sort((a, b) => (a.parcelaAtual ?? 0) - (b.parcelaAtual ?? 0));
  return g;
}

function parcelGroupHasOpenBalance(group: Lancamento[]): boolean {
  return group.some((x) => x.valorPago < x.valor - 1e-9);
}

function lancamentoToUpdatePayload(l: Lancamento, patch: Partial<Omit<Lancamento, 'id'>>): Omit<Lancamento, 'id'> {
  return {
    tipo: l.tipo,
    data: l.data,
    descricao: l.descricao,
    cliente: l.cliente,
    responsavel: l.responsavel,
    categoria: l.categoria,
    planoDeContas: l.planoDeContas,
    metodo: l.metodo,
    valor: l.valor,
    valorPago: l.valorPago,
    vencimento: l.vencimento,
    dataPagamento: l.dataPagamento,
    parcelaAtual: l.parcelaAtual,
    totalParcelas: l.totalParcelas,
    situacao: l.situacao,
    anexo: l.anexo,
    anexoUrl: l.anexoUrl,
    // Preserva o histórico de pagamentos por padrão; o caller pode sobrescrever via patch.
    pagamentos: l.pagamentos ?? [],
    ...patch,
  };
}

function buildPaymentAllocations(sortedGroup: Lancamento[], amount: number): { id: string; valorPago: number }[] {
  let rem = roundMoney2(amount);
  const out: { id: string; valorPago: number }[] = [];
  for (const line of sortedGroup) {
    const owe = roundMoney2(line.valor - line.valorPago);
    if (owe <= 0) continue;
    const d = roundMoney2(Math.min(rem, owe));
    if (d > 0) {
      out.push({ id: line.id, valorPago: roundMoney2(line.valorPago + d) });
      rem = roundMoney2(rem - d);
    }
    if (rem <= 0) break;
  }
  return out;
}

function LancamentoAlocarPagamentoModal({
  anchor,
  allLancamentos,
  metodos,
  busy,
  onClose,
  updateLancamento,
}: {
  anchor: Lancamento | null;
  allLancamentos: Lancamento[];
  metodos: CadastroItem[];
  busy: boolean;
  onClose: () => void;
  updateLancamento: (id: string, payload: Omit<Lancamento, 'id'>) => Promise<boolean>;
}): React.ReactElement | null {
  const [valorInput, setValorInput] = useState('');
  const [responsavelInput, setResponsavelInput] = useState('');
  const [metodoInput, setMetodoInput] = useState('');
  const [dataInput, setDataInput] = useState('');
  const [attempted, setAttempted] = useState(false);
  const metEmpty = metodos.length === 0;

  const group = useMemo(() => (anchor ? getParcelGroup(anchor, allLancamentos) : []), [anchor, allLancamentos]);
  const sorted = useMemo(
    () => [...group].sort((a, b) => (a.parcelaAtual ?? 0) - (b.parcelaAtual ?? 0)),
    [group]
  );

  const totalEmAberto = useMemo(
    () => roundMoney2(sorted.reduce((s, l) => s + Math.max(0, l.valor - l.valorPago), 0)),
    [sorted]
  );

  useEffect(() => {
    if (anchor) {
      setValorInput('');
      setResponsavelInput(anchor.responsavel ?? '');
      setMetodoInput(anchor.metodo ?? '');
      setDataInput('');
      setAttempted(false);
    }
  }, [anchor]);

  useEffect(() => {
    if (!anchor) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [anchor, busy, onClose]);

  if (!anchor) return null;

  const num = parseMoneyInput(valorInput);
  const metodoTrim = metodoInput.trim();
  const erroValor =
    attempted && (num === null || num <= 0)
      ? 'Informe um valor maior que zero.'
      : attempted && num != null && num > totalEmAberto + 1e-9
        ? `O valor não pode superar o em aberto (${formatBRL(totalEmAberto)}).`
        : '';
  const erroMetodo = attempted && !metEmpty && !metodoTrim ? 'Selecione o método de pagamento.' : '';

  const applyCap = num === null ? 0 : Math.min(roundMoney2(num), totalEmAberto);
  const preview = applyCap > 0 ? buildPaymentAllocations(sorted, applyCap) : [];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    if (num === null || num <= 0 || num > totalEmAberto + 1e-9 || totalEmAberto <= 0) return;
    if (!metEmpty && !metodoTrim) return;
    const updates = buildPaymentAllocations(sorted, Math.min(num, totalEmAberto));
    const respTrim = responsavelInput.trim();
    const dataPagto = dataInput.trim() || todayLocalIso();
    for (const u of updates) {
      const line = sorted.find((x) => x.id === u.id)!;
      const delta = roundMoney2(u.valorPago - line.valorPago);
      if (delta <= 0) continue;
      const respFinal = respTrim || line.responsavel || undefined;
      const novoPagamento: Pagamento = {
        valor: delta,
        data: dataPagto,
        responsavel: respFinal,
        metodo: metodoTrim || undefined,
      };
      const novosPagamentos: Pagamento[] = [...(line.pagamentos ?? []), novoPagamento];
      const patch: Partial<Omit<Lancamento, 'id'>> = {
        valorPago: u.valorPago,
        pagamentos: novosPagamentos,
      };
      if (respTrim) patch.responsavel = respTrim;
      const ok = await updateLancamento(u.id, lancamentoToUpdatePayload(line, patch));
      if (!ok) return;
    }
    onClose();
  };

  return ReactDOM.createPortal(
    <div
      role="presentation"
      className="fin-cad-modal__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="fin-aloc-pag-title"
        className="fin-cad-modal__dialog"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="fin-cad-modal__header">
          <h2 className="fin-cad-modal__title" id="fin-aloc-pag-title">
            Registrar pagamento
          </h2>
          <button type="button" className="fin-cad-modal__close" onClick={onClose} disabled={busy} aria-label="Fechar">
            <FiX aria-hidden />
          </button>
        </div>
        <form className="fin-cad-modal__form" onSubmit={submit}>
          <div className="fin-cad-modal__body">
            <p className="fin-aloc-pag__meta">
              Em aberto no grupo: <strong>{formatBRL(totalEmAberto)}</strong>
              {sorted.length > 1 ? ` · ${sorted.length} parcelas` : null}
            </p>
            <div className="fin-table-wrap fin-aloc-pag__mini-table">
              <table className="fin-table fin-table--compact">
                <thead>
                  <tr>
                    <th>Parc.</th>
                    <th>Valor</th>
                    <th>Pago</th>
                    <th>Falta</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((l) => (
                    <tr key={l.id}>
                      <td>
                        {l.parcelaAtual ?? '—'}/{l.totalParcelas ?? '—'}
                      </td>
                      <td className={l.tipo === 'saida' ? 'fin-money--out' : undefined}>
                        {formatBRL(l.tipo === 'saida' ? -l.valor : l.valor)}
                      </td>
                      <td className={l.tipo === 'saida' ? 'fin-money--out' : undefined}>
                        {formatBRL(l.tipo === 'saida' ? -l.valorPago : l.valorPago)}
                      </td>
                      <td className={l.tipo === 'saida' ? 'fin-money--out' : undefined}>
                        {formatBRL(
                          l.tipo === 'saida'
                            ? -Math.max(0, roundMoney2(l.valor - l.valorPago))
                            : Math.max(0, roundMoney2(l.valor - l.valorPago))
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-aloc-val">
                Valor a registrar (R$) <span className="fin-cad-modal__req">*</span>
              </label>
              <input
                id="fin-aloc-val"
                className={`fin-cad-modal__input${erroValor ? ' fin-cad-modal__input--invalid' : ''}`}
                value={valorInput}
                onChange={(e) => setValorInput(e.target.value)}
                placeholder="0,00"
                disabled={busy}
                inputMode="decimal"
                autoComplete="off"
              />
              {erroValor ? <p className="fin-cad-modal__hint fin-cad-modal__hint--error">{erroValor}</p> : null}
            </div>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-aloc-met">
                Método de pagamento
                {!metEmpty ? <span className="fin-cad-modal__req">*</span> : null}
              </label>
              <select
                id="fin-aloc-met"
                className={`fin-cad-modal__input${erroMetodo ? ' fin-cad-modal__input--invalid' : ''}`}
                value={metodoInput}
                onChange={(e) => setMetodoInput(e.target.value)}
                disabled={busy || metEmpty}
              >
                <option value="">Selecione…</option>
                {metodos.map((m) => (
                  <option key={m.id} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </select>
              {erroMetodo ? (
                <p className="fin-cad-modal__hint fin-cad-modal__hint--error">{erroMetodo}</p>
              ) : metEmpty ? (
                <p className="fin-cad-modal__hint">Cadastre métodos de pagamento em Cadastros.</p>
              ) : null}
            </div>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-aloc-data">
                Data do recebimento
              </label>
              <FinDatePicker
                id="fin-aloc-data"
                value={dataInput}
                onChange={setDataInput}
                placeholder="Padrão: hoje"
                disabled={busy}
              />
              <p className="fin-cad-modal__hint">
                Opcional. Vazio usa a data de hoje.
              </p>
            </div>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-aloc-resp">
                Responsável pelo pagamento
              </label>
              <input
                id="fin-aloc-resp"
                type="text"
                className="fin-cad-modal__input"
                value={responsavelInput}
                onChange={(e) => setResponsavelInput(e.target.value)}
                placeholder="Quem paga (opcional)"
                disabled={busy}
                autoComplete="organization"
              />
              <p className="fin-cad-modal__hint">
                Se informado, sobrescreve o responsável das parcelas afetadas.
              </p>
            </div>
            {preview.length > 0 ? (
              <div className="fin-aloc-pag__preview">
                <strong className="fin-aloc-pag__preview-title">Distribuição prevista</strong>
                <ul className="fin-list fin-aloc-pag__preview-list">
                  {preview.map((u) => {
                    const line = sorted.find((x) => x.id === u.id)!;
                    const delta = roundMoney2(u.valorPago - line.valorPago);
                    return (
                      <li key={u.id}>
                        Parc. {line.parcelaAtual}/{line.totalParcelas}: +{formatBRL(delta)} → total pago{' '}
                        {formatBRL(u.valorPago)}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
          <div className="fin-cad-modal__footer">
            <button type="button" className="fin-cad-modal__btn fin-cad-modal__btn--ghost" onClick={onClose} disabled={busy}>
              Cancelar
            </button>
            <button type="submit" className="fin-cad-modal__btn fin-cad-modal__btn--primary" disabled={busy || totalEmAberto <= 0}>
              {busy ? <FinSpinner /> : null}
              {busy ? 'Salvando…' : 'Aplicar pagamento'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

type LancamentoBatchLine = {
  id: string;
  valor: string;
  valorPago: string;
  vencimento: string;
  categoria: string;
  planoDeContas: string;
  metodo: string;
};

function pickRefVencimentoForAppend(prev: LancamentoBatchLine[]): string {
  const last = prev[prev.length - 1]?.vencimento?.trim() ?? '';
  if (last && isValidBatchIsoDate(last)) return last;
  const first = prev[0]?.vencimento?.trim() ?? '';
  if (first && isValidBatchIsoDate(first)) return first;
  return '';
}

function appendBatchLineCloned(prev: LancamentoBatchLine[]): LancamentoBatchLine {
  const base = prev[0];
  const ref = pickRefVencimentoForAppend(prev);
  const vencimento = ref ? addOneMonthIso(ref) : '';
  return {
    id: newBatchLineId(),
    valor: base.valor,
    valorPago: base.valorPago,
    vencimento,
    categoria: base.categoria,
    planoDeContas: base.planoDeContas,
    metodo: base.metodo,
  };
}

function NovoLancamentoForm({
  saving,
  onSalvar,
  onSalvarLote,
  onSalvarEdicao,
  editing,
  onCancelar,
  readOnly,
}: {
  saving: boolean;
  onSalvar: (l: Omit<Lancamento, 'id'>) => void | Promise<void>;
  onSalvarLote: (items: Omit<Lancamento, 'id'>[]) => void | Promise<void>;
  onSalvarEdicao?: (id: string, l: Omit<Lancamento, 'id'>) => void | Promise<void>;
  editing?: Lancamento | null;
  onCancelar: () => void;
  readOnly?: boolean;
}): React.ReactElement {
  const {
    clientes,
    categorias,
    planosDeContasEntrada,
    planosDeContasSaida,
    metodos,
  } = useFinanceiroData();
  const isEditing = Boolean(editing);
  const ro = Boolean(readOnly);
  const hydratingEditRef = useRef(false);
  const prevTipoRef = useRef<'entrada' | 'saida'>(editing?.tipo ?? 'entrada');
  const [modo, setModo] = useState<'unico' | 'lote'>(isEditing ? 'unico' : 'unico');
  const [tipo, setTipo] = useState<'entrada' | 'saida'>(() => editing?.tipo ?? 'entrada');
  const [descricao, setDescricao] = useState('');
  const [cliente, setCliente] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [categoria, setCategoria] = useState('');
  const [planoDeContas, setPlanoDeContas] = useState('');
  const [metodo, setMetodo] = useState('');
  const [valor, setValor] = useState('');
  const [valorPago, setValorPago] = useState('');
  const [quitarIntegral, setQuitarIntegral] = useState(false);
  const [vencimento, setVencimento] = useState('');
  const [dataPagamento, setDataPagamento] = useState('');
  const [pagamentos, setPagamentos] = useState<Pagamento[]>([]);
  const [novoPagValor, setNovoPagValor] = useState('');
  const [novoPagData, setNovoPagData] = useState('');
  const [novoPagResponsavel, setNovoPagResponsavel] = useState('');
  const [novoPagMetodo, setNovoPagMetodo] = useState('');
  const [novoPagError, setNovoPagError] = useState<string | null>(null);
  const [pagamentoRemoverIdx, setPagamentoRemoverIdx] = useState<number | null>(null);
  const [pagamentoEditarIdx, setPagamentoEditarIdx] = useState<number | null>(null);
  const [parcelaAtual, setParcelaAtual] = useState('');
  const [totalParcelas, setTotalParcelas] = useState('');
  const [anexo, setAnexo] = useState<string | undefined>();
  const [anexoFile, setAnexoFile] = useState<File | null>(null);
  const [batchLines, setBatchLines] = useState<LancamentoBatchLine[]>(() => [
    { id: newBatchLineId(), valor: '', valorPago: '', vencimento: '', categoria: '', planoDeContas: '', metodo: '' },
  ]);
  const comprovanteInputRef = useRef<HTMLInputElement>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!editing) return;
    hydratingEditRef.current = true;
    setModo('unico');
    setTipo(editing.tipo);
    setDescricao(editing.descricao);
    setCliente(editing.cliente ?? '');
    setResponsavel(editing.responsavel ?? '');
    setCategoria(editing.categoria ?? '');
    setPlanoDeContas(editing.planoDeContas ?? '');
    setMetodo(editing.metodo ?? '');
    setValor(decimalToBrInput(editing.valor));
    setValorPago(editing.valorPago > 0 ? decimalToBrInput(editing.valorPago) : '');
    setVencimento(editing.vencimento ?? '');
    setDataPagamento(editing.dataPagamento ?? '');
    setPagamentos((editing.pagamentos ?? []).map((p) => ({ ...p })));
    setNovoPagValor('');
    setNovoPagData('');
    setNovoPagResponsavel('');
    setNovoPagError(null);
    setParcelaAtual(editing.parcelaAtual != null ? String(editing.parcelaAtual) : '');
    setTotalParcelas(editing.totalParcelas != null ? String(editing.totalParcelas) : '');
    setAnexo(editing.anexo);
    setAnexoFile(null);
    setSubmitAttempted(false);
    setTouched({});
    setQuitarIntegral(
      editing.situacao === 'pago' || editing.valorPago >= editing.valor - 1e-9
    );
    setBatchLines([{ id: newBatchLineId(), valor: '', valorPago: '', vencimento: '', categoria: '', planoDeContas: '', metodo: '' }]);
    if (comprovanteInputRef.current) comprovanteInputRef.current.value = '';
  }, [editing]);

  useEffect(() => {
    if (editing) return;
    setQuitarIntegral(false);
    setPagamentos([]);
    setNovoPagValor('');
    setNovoPagData('');
    setNovoPagResponsavel('');
    setNovoPagError(null);
  }, [editing]);

  useEffect(() => {
    if (modo === 'lote' && !isEditing) setQuitarIntegral(false);
  }, [modo, isEditing]);

  useEffect(() => {
    if (hydratingEditRef.current) {
      hydratingEditRef.current = false;
      prevTipoRef.current = tipo;
      return;
    }
    if (prevTipoRef.current === tipo) return;
    prevTipoRef.current = tipo;
    setPlanoDeContas('');
    setBatchLines((prev) => prev.map((l) => ({ ...l, planoDeContas: '' })));
    setTouched({});
    setSubmitAttempted(false);
  }, [tipo]);

  const categoriasAtivas = categorias;
  const planosAtivos = tipo === 'entrada' ? planosDeContasEntrada : planosDeContasSaida;
  const catEmpty = categoriasAtivas.length === 0;
  const planoEmpty = planosAtivos.length === 0;
  const metEmpty = metodos.length === 0;

  const touch = (key: string) => setTouched((p) => ({ ...p, [key]: true }));

  // ===== Pagamentos parciais (modo único + edição) =====
  const valorAtualNum = parseMoneyInput(valor) ?? 0;
  const valorPagoTotal = pagamentos.reduce((s, p) => s + (Number(p.valor) || 0), 0);
  const saldoRestante = roundMoney2(Math.max(0, valorAtualNum - valorPagoTotal));

  useEffect(() => {
    if (metodo) setNovoPagMetodo((prev) => prev || metodo);
  }, [metodo]);

  const addPagamento = useCallback(() => {
    if (ro) return;
    setNovoPagError(null);
    const v = parseMoneyInput(novoPagValor);
    if (v == null || v <= 0) {
      setNovoPagError('Informe um valor maior que zero.');
      return;
    }
    if (valorAtualNum > 0 && v > saldoRestante + 1e-9) {
      setNovoPagError(`Excede o saldo (${formatBRL(saldoRestante)}).`);
      return;
    }
    const met = novoPagMetodo.trim() || metodo.trim();
    if (!metEmpty && !met) {
      setNovoPagError('Selecione o método de pagamento.');
      return;
    }
    const data = novoPagData.trim() || todayLocalIso();
    const resp = novoPagResponsavel.trim() || responsavel.trim() || undefined;
    setPagamentos((prev) => [...prev, { valor: roundMoney2(v), data, responsavel: resp, metodo: met || undefined }]);
    setNovoPagValor('');
    setNovoPagData('');
    setNovoPagResponsavel('');
    setNovoPagMetodo('');
  }, [ro, novoPagValor, novoPagData, novoPagResponsavel, novoPagMetodo, responsavel, metodo, metEmpty, valorAtualNum, saldoRestante]);

  const removePagamento = useCallback((idx: number) => {
    if (ro) return;
    setPagamentos((prev) => prev.filter((_, i) => i !== idx));
  }, [ro]);

  const editPagamento = useCallback((idx: number, patch: Pagamento) => {
    if (ro) return;
    setPagamentos((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  }, [ro]);

  const quitarSaldoAgora = useCallback(() => {
    if (ro || saldoRestante <= 0) return;
    const met = metodo.trim();
    if (!metEmpty && !met) {
      setNovoPagError('Selecione o método de pagamento no lançamento antes de quitar o saldo.');
      return;
    }
    const data = todayLocalIso();
    const resp = responsavel.trim() || undefined;
    setPagamentos((prev) => [...prev, { valor: saldoRestante, data, responsavel: resp, metodo: met || undefined }]);
    setNovoPagError(null);
  }, [ro, saldoRestante, responsavel, metodo, metEmpty]);

  const updateBatchLine = (id: string, patch: Partial<Omit<LancamentoBatchLine, 'id'>>) => {
    setBatchLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const cliEmpty = clientes.length === 0;

  const validation = useMemo(() => {
    const e: Record<string, string> = {};
    if (tipo === 'entrada') {
      if (!catEmpty && (modo === 'unico' || isEditing) && !categoria) {
        e.categoria = 'Selecione uma categoria.';
      }
      if (!planoEmpty && (modo === 'unico' || isEditing) && !planoDeContas) {
        e.planoDeContas = 'Selecione um plano de contas.';
      }
    }
    if (!metEmpty && (modo === 'unico' || isEditing) && !metodo) e.metodo = 'Selecione o método de pagamento.';
    if (tipo === 'entrada' && !cliEmpty && !cliente) e.cliente = 'Selecione o cliente.';

    if (modo === 'unico' || isEditing) {
      const vNum = parseMoneyInput(valor);
      if (vNum === null || vNum <= 0) e.valor = 'Informe um valor maior que zero.';
      const totalPag = pagamentos.reduce((s, p) => s + (Number(p.valor) || 0), 0);
      if (vNum !== null && totalPag > vNum + 1e-9) {
        e.pagamentos = `Soma dos pagamentos (${formatBRL(totalPag)}) excede o valor do lançamento.`;
      }
      if (!metEmpty && pagamentos.some((p) => !(p.metodo?.trim() || metodo.trim()))) {
        e.pagamentosMetodo = `Informe o método de pagamento em todos os ${tipo === 'entrada' ? 'recebimentos' : 'pagamentos'}.`;
      }
    } else {
      batchLines.forEach((line, idx) => {
        const vKey = `batchLine-${line.id}-valor`;
        const vpKey = `batchLine-${line.id}-vp`;
        const catKey = `batchLine-${line.id}-cat`;
        const planoKey = `batchLine-${line.id}-plano`;
        const metKey = `batchLine-${line.id}-met`;
        const vNum = parseMoneyInput(line.valor);
        if (vNum === null || vNum <= 0) {
          e[vKey] = `Parcela ${idx + 1}: informe um valor maior que zero.`;
        }
        if (tipo === 'entrada') {
          if (!catEmpty && !line.categoria) {
            e[catKey] = `Parcela ${idx + 1}: selecione uma categoria.`;
          }
          if (!planoEmpty && !line.planoDeContas) {
            e[planoKey] = `Parcela ${idx + 1}: selecione um plano de contas.`;
          }
        }
        if (!metEmpty && !line.metodo) {
          e[metKey] = `Parcela ${idx + 1}: selecione o método de pagamento.`;
        }
        const vencKey = `batchLine-${line.id}-venc`;
        const vRaw = line.vencimento?.trim() ?? '';
        if (!vRaw || !isValidBatchIsoDate(vRaw)) {
          e[vencKey] = `Parcela ${idx + 1}: informe o vencimento.`;
        }
        if (line.valorPago.trim() !== '') {
          const vp = parseMoneyInput(line.valorPago);
          if (vp === null || vp < 0) e[vpKey] = 'Valor pago inválido.';
          else if (vNum !== null && vp > vNum) e[vpKey] = 'Não pode ser maior que o valor da parcela.';
        }
      });
    }
    return e;
  }, [categoria, planoDeContas, metodo, cliente, tipo, valor, valorPago, pagamentos, catEmpty, planoEmpty, metEmpty, cliEmpty, modo, batchLines, isEditing]);

  const showErr = (key: string) => Boolean(validation[key] && (touched[key] || submitAttempted));

  const listBlocked = metEmpty || (tipo === 'entrada' && (cliEmpty || catEmpty || planoEmpty));

  const descricaoParaEnvioSimples = (): string => descricao.trim();

  const descricaoBaseLote = (): string => descricao.trim();

  const montarDescricaoParcela = (base: string, i: number, n: number): string => {
    if (n <= 1) return base;
    const b = base.trim();
    if (b) return `${b} — Parcela ${i + 1}/${n}`;
    return `Parcela ${i + 1}/${n}`;
  };

  const renderDocumentoComprovante = (opts?: { loteHint?: boolean }): React.ReactElement => (
    <section className="fin-lanc-section fin-lanc-section--last" aria-labelledby="fin-lanc-h-doc">
      <h3 className="fin-lanc-section__title" id="fin-lanc-h-doc">
        Documento
      </h3>
      {opts?.loteHint ? (
        <p className="fin-lanc-section__hint">
          Comprovante opcional. O arquivo é gravado na primeira parcela do lote; as demais podem ser adicionadas posteriormente ao editar a parcela.
        </p>
      ) : null}
      <div className="fin-lanc-section__grid">
        <div className="fin-field-block fin-span-12">
          <FinFieldTitle htmlFor={ro ? undefined : 'fin-lanc-file'}>Comprovante ou NF (opcional)</FinFieldTitle>
          {editing?.anexoUrl && !anexoFile ? (
            <a
              href={editing.anexoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="fin-lanc-comprovante-link fin-lanc-comprovante-link--block"
            >
              Visualizar comprovante
            </a>
          ) : null}
          {ro ? (
            !editing?.anexoUrl ? (
              editing?.anexo ? (
                <p className="fin-card__hint">Arquivo registrado (sem link público): {editing.anexo}</p>
              ) : (
                <p className="fin-card__hint">Nenhum comprovante anexado.</p>
              )
            ) : null
          ) : (
            <div className="fin-comprovante">
              <input
                ref={comprovanteInputRef}
                id="fin-lanc-file"
                type="file"
                className="fin-comprovante__input"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,application/pdf,image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setAnexoFile(f);
                  if (f) setAnexo(f.name);
                  else setAnexo(isEditing ? editing?.anexo : undefined);
                }}
              />
              <div className="fin-comprovante__ui">
                <label htmlFor="fin-lanc-file" className="fin-comprovante__pick">
                  Anexar arquivo
                </label>
                <span className={`fin-comprovante__filename${anexo ? ' fin-comprovante__filename--selected' : ''}`}>
                  {anexo ?? 'Nenhum arquivo'}
                </span>
                {anexo ? (
                  <button
                    type="button"
                    className="fin-icon-btn fin-icon-btn--ghost fin-comprovante__clear-icon"
                    onClick={() => {
                      setAnexoFile(null);
                      setAnexo(isEditing ? editing?.anexo : undefined);
                      if (comprovanteInputRef.current) comprovanteInputRef.current.value = '';
                    }}
                    aria-label="Remover arquivo selecionado"
                  >
                    <FiTrash2 aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );

  const pagamentoEmRemocao = pagamentoRemoverIdx != null ? pagamentos[pagamentoRemoverIdx] : null;
  const pagamentoEmEdicao = pagamentoEditarIdx != null ? pagamentos[pagamentoEditarIdx] : null;

  return (
    <>
    <form
      className="fin-form fin-form--lanc"
      onSubmit={async (e) => {
        e.preventDefault();
        if (ro) return;
        if (saving || listBlocked) return;
        setSubmitAttempted(true);
        if (Object.keys(validation).length) return;

        let anexoBase64: string | undefined;
        let anexoContentType: string | undefined;
        if (anexoFile) {
          const part = await readFileAsBase64Part(anexoFile);
          anexoBase64 = part.base64;
          anexoContentType = part.contentType;
        }

        const nomeArquivo = anexoFile ? anexoFile.name : anexo;

        if (isEditing && editing) {
          const vNum = parseMoneyInput(valor)!;
          const valorPagoFinal = roundMoney2(pagamentos.reduce((s, p) => s + (Number(p.valor) || 0), 0));
          void onSalvarEdicao?.(editing.id, {
            tipo,
            descricao: descricaoParaEnvioSimples(),
            cliente: tipo === 'entrada' ? cliente.trim() : undefined,
            responsavel: responsavel.trim() ? responsavel.trim() : undefined,
            categoria: categoria.trim() || undefined,
            planoDeContas: planoDeContas.trim() || undefined,
            metodo: metodo.trim(),
            valor: vNum,
            valorPago: valorPagoFinal,
            data: resolveFinanceiroDataCompetencia(vencimento),
            vencimento: vencimento.trim() || undefined,
            parcelaAtual: parcelaAtual ? Number(parcelaAtual) : undefined,
            totalParcelas: totalParcelas ? Number(totalParcelas) : undefined,
            situacao: editing.situacao,
            anexo: nomeArquivo,
            anexoUrl: editing.anexoUrl,
            anexoBase64,
            anexoContentType,
            pagamentos,
          });
          return;
        }
        if (modo === 'unico') {
          const vNum = parseMoneyInput(valor)!;
          const valorPagoFinal = roundMoney2(pagamentos.reduce((s, p) => s + (Number(p.valor) || 0), 0));
          void onSalvar({
            tipo,
            descricao: descricaoParaEnvioSimples(),
            cliente: tipo === 'entrada' ? cliente.trim() : undefined,
            responsavel: responsavel.trim() ? responsavel.trim() : undefined,
            categoria: categoria.trim() || undefined,
            planoDeContas: planoDeContas.trim() || undefined,
            metodo: metodo.trim(),
            valor: vNum,
            valorPago: valorPagoFinal,
            data: resolveFinanceiroDataCompetencia(vencimento),
            vencimento: vencimento.trim() || undefined,
            parcelaAtual: parcelaAtual ? Number(parcelaAtual) : undefined,
            totalParcelas: totalParcelas ? Number(totalParcelas) : undefined,
            situacao: 'pendente',
            anexo: nomeArquivo,
            anexoBase64,
            anexoContentType,
            pagamentos,
          });
        } else {
          const n = batchLines.length;
          const baseDesc = descricaoBaseLote();
          const items: Omit<Lancamento, 'id'>[] = batchLines.map((line, i) => {
            const vNum = parseMoneyInput(line.valor)!;
            const vpRaw = parseMoneyInput(line.valorPago);
            const valorPagoFinal = vpRaw !== null && line.valorPago.trim() !== '' ? vpRaw : 0;
            const desc = montarDescricaoParcela(baseDesc, i, n);
            const isFirst = i === 0;
            const nomeLote = isFirst ? nomeArquivo : undefined;
            const dataLinha = line.vencimento.trim();
            return {
              tipo,
              descricao: desc,
              cliente: tipo === 'entrada' ? cliente.trim() : undefined,
              responsavel: responsavel.trim() ? responsavel.trim() : undefined,
              categoria: line.categoria.trim() || undefined,
              planoDeContas: line.planoDeContas.trim() || undefined,
              metodo: line.metodo.trim(),
              valor: vNum,
              valorPago: valorPagoFinal,
              data: dataLinha,
              vencimento: dataLinha || undefined,
              parcelaAtual: i + 1,
              totalParcelas: n,
              situacao: 'pendente',
              anexo: nomeLote,
              anexoBase64: isFirst ? anexoBase64 : undefined,
              anexoContentType: isFirst ? anexoContentType : undefined,
            };
          });
          void onSalvarLote(items);
        }
      }}
    >
      <fieldset className="fin-lanc-fieldset" disabled={ro}>
      {!isEditing ? (
        <div className="fin-lanc-section fin-lanc-section--flush" role="region" aria-label="Modo de inclusão">
          <div className="fin-segmented fin-lanc-modo" role="tablist" aria-label="Modo de lançamento">
            <button
              type="button"
              role="tab"
              aria-selected={modo === 'unico'}
              className={`fin-segmented__btn${modo === 'unico' ? ' fin-segmented__btn--active' : ''}`}
              onClick={() => {
                setModo('unico');
                setSubmitAttempted(false);
              }}
            >
              Um lançamento
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={modo === 'lote'}
              className={`fin-segmented__btn${modo === 'lote' ? ' fin-segmented__btn--active' : ''}`}
              onClick={() => {
                setModo('lote');
                setBatchLines([{ id: newBatchLineId(), valor: '', valorPago: '', vencimento: '', categoria: '', planoDeContas: '', metodo: '' }]);
                setSubmitAttempted(false);
              }}
            >
              Em lote (parcelas)
            </button>
          </div>
        </div>
      ) : null}

      {(() => {
        const isLoteNovo = modo === 'lote' && !isEditing;
        const hasVenc = modo === 'unico' || isEditing;
        const tipoSpan = isLoteNovo ? 'fin-span-12' : hasVenc ? 'fin-span-6' : 'fin-span-12';
        const vencSpan = 'fin-span-6';
        return (
          <section className="fin-lanc-section" aria-labelledby="fin-lanc-h-datas">
            <h3 className="fin-lanc-section__title" id="fin-lanc-h-datas">
              {isLoteNovo ? 'Tipo' : 'Tipo e vencimento'}
            </h3>
            <div className="fin-lanc-section__grid">
              <div className={`fin-field-block ${tipoSpan}`}>
                <FinFieldTitle>Tipo</FinFieldTitle>
                <select
                  id="fin-lanc-tipo"
                  className="fin-ctrl"
                  value={tipo}
                  onChange={(e) => {
                    setTipo(e.target.value as 'entrada' | 'saida');
                    touch('tipo');
                  }}
                >
                  <option value="entrada">Entrada</option>
                  <option value="saida">Saída</option>
                </select>
              </div>
              {hasVenc ? (
                <div className={`fin-field-block ${vencSpan}`}>
                  <FinFieldTitle>Vencimento</FinFieldTitle>
                  <FinDatePicker
                    value={vencimento}
                    onChange={(iso) => {
                      setVencimento(iso);
                      touch('vencimento');
                    }}
                    placeholder="Opcional"
                  />
                </div>
              ) : null}
            </div>
          </section>
        );
      })()}

      <section className="fin-lanc-section" aria-labelledby="fin-lanc-h-classif">
        <h3 className="fin-lanc-section__title" id="fin-lanc-h-classif">
          Classificação
        </h3>
        <div className={`fin-lanc-classif-card fin-lanc-classif-card--${tipo}`}>
          <p className="fin-lanc-classif-card__kicker">
            Categoria é a mesma lista para entradas e saídas; o plano de contas segue o tipo de lançamento.
          </p>
          <div className="fin-lanc-section__grid">
          <div className="fin-field-block fin-span-12">
            <FinFieldTitle htmlFor="fin-lanc-desc">Descrição</FinFieldTitle>
            <input
              id="fin-lanc-desc"
              className="fin-ctrl"
              value={descricao}
              onChange={(e) => {
                setDescricao(e.target.value);
                touch('descricao');
              }}
              onBlur={() => touch('descricao')}
              placeholder={tipo === 'entrada' ? 'Opcional — ex.: honorários, NF' : 'Opcional — ex.: aluguel, energia'}
              autoComplete="off"
            />
          </div>
          {modo === 'unico' || isEditing ? (
            <>
              <div className="fin-field-block fin-span-4">
                <FinFieldTitle required={tipo === 'entrada'}>Categoria</FinFieldTitle>
                <select
                  className={`fin-ctrl${showErr('categoria') ? ' fin-ctrl--error' : ''}`}
                  value={categoria}
                  onChange={(e) => {
                    setCategoria(e.target.value);
                    touch('categoria');
                  }}
                  onBlur={() => touch('categoria')}
                  disabled={catEmpty}
                  aria-label="Categoria"
                >
                  <option value="">Selecione…</option>
                  {categoriasAtivas.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {showErr('categoria') ? (
                  <span className="fin-field-msg fin-field-msg--error">{validation.categoria}</span>
                ) : null}
              </div>
              <div className="fin-field-block fin-span-4">
                <FinFieldTitle required={tipo === 'entrada'}>Plano de contas</FinFieldTitle>
                <select
                  className={`fin-ctrl${showErr('planoDeContas') ? ' fin-ctrl--error' : ''}`}
                  value={planoDeContas}
                  onChange={(e) => {
                    setPlanoDeContas(e.target.value);
                    touch('planoDeContas');
                  }}
                  onBlur={() => touch('planoDeContas')}
                  disabled={planoEmpty}
                  aria-label="Plano de contas"
                >
                  <option value="">Selecione…</option>
                  {planosAtivos.map((c) => (
                    <option key={c.id} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {showErr('planoDeContas') ? (
                  <span className="fin-field-msg fin-field-msg--error">{validation.planoDeContas}</span>
                ) : null}
              </div>
              <div className="fin-field-block fin-span-4">
                <FinFieldTitle required>Método de pagamento</FinFieldTitle>
                <select
                  className={`fin-ctrl${showErr('metodo') ? ' fin-ctrl--error' : ''}`}
                  value={metodo}
                  onChange={(e) => {
                    setMetodo(e.target.value);
                    touch('metodo');
                  }}
                  onBlur={() => touch('metodo')}
                  disabled={metEmpty}
                >
                  <option value="">Selecione…</option>
                  {metodos.map((m) => (
                    <option key={m.id} value={m.name}>
                      {m.name}
                    </option>
                  ))}
                </select>
                {showErr('metodo') ? (
                  <span className="fin-field-msg fin-field-msg--error">{validation.metodo}</span>
                ) : null}
              </div>
            </>
          ) : null}
          </div>
        </div>
      </section>

      <section className="fin-lanc-section" aria-labelledby="fin-lanc-h-partes">
        <h3 className="fin-lanc-section__title" id="fin-lanc-h-partes">
          {tipo === 'entrada' ? 'Cliente e responsável' : 'Responsável'}
        </h3>
        <div className="fin-lanc-section__grid">
          {tipo === 'entrada' ? (
            <div className="fin-field-block fin-span-6">
              <FinFieldTitle required>Cliente</FinFieldTitle>
              <select
                className={`fin-ctrl${showErr('cliente') ? ' fin-ctrl--error' : ''}`}
                value={cliente}
                onChange={(e) => {
                  setCliente(e.target.value);
                  touch('cliente');
                }}
                onBlur={() => touch('cliente')}
                disabled={cliEmpty}
              >
                <option value="">Selecione…</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              {showErr('cliente') ? (
                <span className="fin-field-msg fin-field-msg--error">{validation.cliente}</span>
              ) : null}
            </div>
          ) : null}
          <div className={`fin-field-block ${tipo === 'saida' ? 'fin-span-12' : 'fin-span-6'}`}>
            <FinFieldTitle htmlFor="fin-lanc-resp">Responsável</FinFieldTitle>
            <input
              id="fin-lanc-resp"
              type="text"
              className="fin-ctrl"
              value={responsavel}
              onChange={(e) => {
                setResponsavel(e.target.value);
                touch('responsavel');
              }}
              onBlur={() => touch('responsavel')}
              placeholder="Quem paga (opcional)"
              autoComplete="organization"
            />
          </div>
        </div>
      </section>

      {modo === 'lote' && !isEditing ? (
        <section className="fin-lanc-section" aria-labelledby="fin-lanc-h-lote">
          <h3 className="fin-lanc-section__title" id="fin-lanc-h-lote">
            Parcelas
          </h3>
          <p className="fin-lanc-section__hint">
            Cada linha gera um lançamento; informe o vencimento de cada parcela (usa a mesma data na competência e no vencimento). Categoria, plano de contas (em entradas), método e valores são por parcela.
          </p>
          <div className="fin-batch-lines">
            {batchLines.map((line, idx) => (
              <div key={line.id} className="fin-batch-line">
                <span className="fin-batch-line__label">Parcela {idx + 1}</span>
                <div className="fin-field-block">
                  <FinFieldTitle required>Valor (R$)</FinFieldTitle>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`fin-ctrl${showErr(`batchLine-${line.id}-valor`) ? ' fin-ctrl--error' : ''}`}
                    value={line.valor}
                    onChange={(ev) => updateBatchLine(line.id, { valor: ev.target.value })}
                    onBlur={() => touch(`batchLine-${line.id}-valor`)}
                    placeholder="0,00"
                    aria-label={`Valor parcela ${idx + 1}`}
                  />
                  {showErr(`batchLine-${line.id}-valor`) ? (
                    <span className="fin-field-msg fin-field-msg--error">{validation[`batchLine-${line.id}-valor`]}</span>
                  ) : null}
                </div>
                <div className="fin-field-block">
                  <FinFieldTitle>Já pago (R$)</FinFieldTitle>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`fin-ctrl${showErr(`batchLine-${line.id}-vp`) ? ' fin-ctrl--error' : ''}`}
                    value={line.valorPago}
                    onChange={(ev) => updateBatchLine(line.id, { valorPago: ev.target.value })}
                    onBlur={() => touch(`batchLine-${line.id}-vp`)}
                    placeholder="Opcional — vazio = 0"
                    aria-label={`Valor pago parcela ${idx + 1}`}
                  />
                  {showErr(`batchLine-${line.id}-vp`) ? (
                    <span className="fin-field-msg fin-field-msg--error">{validation[`batchLine-${line.id}-vp`]}</span>
                  ) : null}
                </div>
                <div className="fin-field-block">
                  <FinFieldTitle required={tipo === 'entrada'}>Categoria</FinFieldTitle>
                  <select
                    className={`fin-ctrl${showErr(`batchLine-${line.id}-cat`) ? ' fin-ctrl--error' : ''}`}
                    value={line.categoria}
                    onChange={(ev) => updateBatchLine(line.id, { categoria: ev.target.value })}
                    onBlur={() => touch(`batchLine-${line.id}-cat`)}
                    disabled={catEmpty}
                    aria-label={`Categoria parcela ${idx + 1}`}
                  >
                    <option value="">Selecione…</option>
                    {categoriasAtivas.map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {showErr(`batchLine-${line.id}-cat`) ? (
                    <span className="fin-field-msg fin-field-msg--error">{validation[`batchLine-${line.id}-cat`]}</span>
                  ) : null}
                </div>
                <div className="fin-field-block">
                  <FinFieldTitle required={tipo === 'entrada'}>Plano de contas</FinFieldTitle>
                  <select
                    className={`fin-ctrl${showErr(`batchLine-${line.id}-plano`) ? ' fin-ctrl--error' : ''}`}
                    value={line.planoDeContas}
                    onChange={(ev) => updateBatchLine(line.id, { planoDeContas: ev.target.value })}
                    onBlur={() => touch(`batchLine-${line.id}-plano`)}
                    disabled={planoEmpty}
                    aria-label={`Plano de contas parcela ${idx + 1}`}
                  >
                    <option value="">Selecione…</option>
                    {planosAtivos.map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {showErr(`batchLine-${line.id}-plano`) ? (
                    <span className="fin-field-msg fin-field-msg--error">{validation[`batchLine-${line.id}-plano`]}</span>
                  ) : null}
                </div>
                <div className="fin-field-block">
                  <FinFieldTitle required>Método de pagamento</FinFieldTitle>
                  <select
                    className={`fin-ctrl${showErr(`batchLine-${line.id}-met`) ? ' fin-ctrl--error' : ''}`}
                    value={line.metodo}
                    onChange={(ev) => updateBatchLine(line.id, { metodo: ev.target.value })}
                    onBlur={() => touch(`batchLine-${line.id}-met`)}
                    disabled={metEmpty}
                    aria-label={`Método de pagamento — parcela ${idx + 1}`}
                  >
                    <option value="">Selecione…</option>
                    {metodos.map((m) => (
                      <option key={m.id} value={m.name}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  {showErr(`batchLine-${line.id}-met`) ? (
                    <span className="fin-field-msg fin-field-msg--error">{validation[`batchLine-${line.id}-met`]}</span>
                  ) : null}
                </div>
                <div className="fin-field-block">
                  <FinFieldTitle required>Vencimento</FinFieldTitle>
                  <FinDatePicker
                    value={line.vencimento}
                    onChange={(iso) => updateBatchLine(line.id, { vencimento: iso })}
                    placeholder="Data da parcela"
                    hasError={showErr(`batchLine-${line.id}-venc`)}
                    onTriggerBlur={() => touch(`batchLine-${line.id}-venc`)}
                  />
                  {showErr(`batchLine-${line.id}-venc`) ? (
                    <span className="fin-field-msg fin-field-msg--error">{validation[`batchLine-${line.id}-venc`]}</span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="fin-icon-btn fin-icon-btn--ghost fin-batch-line__remove"
                  disabled={saving || batchLines.length <= 1}
                  onClick={() => setBatchLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.id !== line.id)))}
                  aria-label={`Remover parcela ${idx + 1}`}
                >
                  <FiTrash2 aria-hidden />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="fin-btn fin-btn--ghost fin-btn--small"
            disabled={saving}
            style={{ marginTop: '0.5rem' }}
            onClick={() => setBatchLines((prev) => [...prev, appendBatchLineCloned(prev)])}
          >
            + Adicionar parcela
          </button>
        </section>
      ) : null}

      {modo === 'lote' && !isEditing ? renderDocumentoComprovante({ loteHint: true }) : null}

      {modo === 'unico' || isEditing ? (
        <>
          <section className="fin-lanc-section" aria-labelledby="fin-lanc-h-valores">
            <h3 className="fin-lanc-section__title" id="fin-lanc-h-valores">
              Valores
            </h3>
            <div className="fin-lanc-section__grid">
              {ro && editing ? (
                <div className="fin-field-block fin-span-12">
                  <FinFieldTitle>Situação</FinFieldTitle>
                  <span className={situacaoBadgeClass(editing.situacao)} role="status">
                    {situacaoBadgeLabel(editing.situacao)}
                  </span>
                </div>
              ) : null}
              <div className={`fin-field-block ${isEditing ? 'fin-span-6' : 'fin-span-12'}`}>
                <FinFieldTitle required htmlFor="fin-lanc-valor">
                  Valor (R$)
                </FinFieldTitle>
                <input
                  id="fin-lanc-valor"
                  type="text"
                  inputMode="decimal"
                  className={`fin-ctrl${showErr('valor') ? ' fin-ctrl--error' : ''}`}
                  value={valor}
                  onChange={(e) => {
                    setValor(e.target.value);
                    touch('valor');
                  }}
                  onBlur={() => touch('valor')}
                  placeholder="0,00"
                />
                {showErr('valor') ? (
                  <span className="fin-field-msg fin-field-msg--error">{validation.valor}</span>
                ) : null}
              </div>
              {isEditing ? (
                <div className="fin-field-block fin-span-6">
                  <FinFieldTitle>Parcelas (atual / total)</FinFieldTitle>
                  <div className="fin-form__inline">
                    <input
                      type="text"
                      inputMode="numeric"
                      className="fin-ctrl"
                      value={parcelaAtual}
                      onChange={(e) => setParcelaAtual(e.target.value.replace(/\D/g, ''))}
                      placeholder="Atual"
                      aria-label="Parcela atual"
                    />
                    <span className="fin-form__slash" aria-hidden>
                      /
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="fin-ctrl"
                      value={totalParcelas}
                      onChange={(e) => setTotalParcelas(e.target.value.replace(/\D/g, ''))}
                      placeholder="Total"
                      aria-label="Total de parcelas"
                    />
                  </div>
                </div>
              ) : null}
              {(modo === 'unico' || isEditing) ? (
                <div className="fin-field-block fin-span-12 fin-pag-section">
                  <div className="fin-pag-section__heading">
                    <FinFieldTitle>
                      {tipo === 'entrada' ? 'Recebimentos parciais' : 'Pagamentos parciais'}
                    </FinFieldTitle>
                    <span className="fin-pag-summary">
                      {formatBRL(roundMoney2(valorPagoTotal))}
                      {' / '}
                      {formatBRL(roundMoney2(valorAtualNum))}
                      {valorAtualNum > 0 ? (
                        <>
                          {' '}
                          · saldo <strong>{formatBRL(saldoRestante)}</strong>
                        </>
                      ) : null}
                    </span>
                  </div>
                  {validation.pagamentos && submitAttempted ? (
                    <span className="fin-field-msg fin-field-msg--error">{validation.pagamentos}</span>
                  ) : null}
                  {validation.pagamentosMetodo && submitAttempted ? (
                    <span className="fin-field-msg fin-field-msg--error">{validation.pagamentosMetodo}</span>
                  ) : null}
                  {pagamentos.length === 0 ? (
                    <p className="fin-pag-empty">
                      Nenhum {tipo === 'entrada' ? 'recebimento' : 'pagamento'} registrado.
                    </p>
                  ) : (
                    <ul className="fin-pag-list" aria-label="Pagamentos registrados">
                      {pagamentos.map((p, idx) => (
                        <li key={`${p.id ?? 'novo'}-${idx}`} className="fin-pag-item">
                          <span className="fin-pag-item__valor">{formatBRL(p.valor)}</span>
                          <span className="fin-pag-item__data">
                            {new Date(p.data + 'T12:00:00').toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })}
                          </span>
                          <span className="fin-pag-item__met">{p.metodo?.trim() || metodo || '—'}</span>
                          <span className="fin-pag-item__resp">{p.responsavel || '—'}</span>
                          {!ro ? (
                            <div className="fin-pag-item__actions">
                              <button
                                type="button"
                                className="fin-icon-btn fin-icon-btn--ghost"
                                onClick={() => setPagamentoEditarIdx(idx)}
                                aria-label={`Editar pagamento de ${formatBRL(p.valor)}`}
                                title="Editar"
                              >
                                <FiEdit2 aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="fin-icon-btn fin-icon-btn--danger"
                                onClick={() => setPagamentoRemoverIdx(idx)}
                                aria-label={`Remover pagamento de ${formatBRL(p.valor)}`}
                                title="Remover"
                              >
                                <FiTrash2 aria-hidden />
                              </button>
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!ro ? (
                    <div className="fin-pag-add">
                      <div className="fin-pag-add__row">
                        <div className="fin-pag-add__field fin-pag-add__field--valor">
                          <FinFieldTitle htmlFor="fin-pag-novo-valor">Valor (R$)</FinFieldTitle>
                          <input
                            id="fin-pag-novo-valor"
                            type="text"
                            inputMode="decimal"
                            className="fin-ctrl"
                            value={novoPagValor}
                            onChange={(e) => {
                              setNovoPagValor(e.target.value);
                              if (novoPagError) setNovoPagError(null);
                            }}
                            placeholder="0,00"
                          />
                        </div>
                        <div className="fin-pag-add__field fin-pag-add__field--data">
                          <FinFieldTitle>Data</FinFieldTitle>
                          <FinDatePicker
                            value={novoPagData}
                            onChange={(iso) => {
                              setNovoPagData(iso);
                              if (novoPagError) setNovoPagError(null);
                            }}
                            placeholder="Hoje"
                          />
                        </div>
                        <div className="fin-pag-add__field fin-pag-add__field--met">
                          <FinFieldTitle required={!metEmpty} htmlFor="fin-pag-novo-met">
                            Método de pagamento
                          </FinFieldTitle>
                          <select
                            id="fin-pag-novo-met"
                            className="fin-ctrl"
                            value={novoPagMetodo}
                            onChange={(e) => {
                              setNovoPagMetodo(e.target.value);
                              if (novoPagError) setNovoPagError(null);
                            }}
                            disabled={metEmpty}
                            aria-label="Método de pagamento"
                          >
                            <option value="">{metodo.trim() || 'Selecione…'}</option>
                            {metodos.map((m) => (
                              <option key={m.id} value={m.name}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="fin-pag-add__field fin-pag-add__field--resp">
                          <FinFieldTitle htmlFor="fin-pag-novo-resp">Responsável</FinFieldTitle>
                          <input
                            id="fin-pag-novo-resp"
                            type="text"
                            className="fin-ctrl"
                            value={novoPagResponsavel}
                            onChange={(e) => {
                              setNovoPagResponsavel(e.target.value);
                              if (novoPagError) setNovoPagError(null);
                            }}
                            placeholder={responsavel.trim() || 'Opcional'}
                          />
                        </div>
                        <button
                          type="button"
                          className="fin-btn fin-btn--ghost fin-pag-add__btn"
                          onClick={addPagamento}
                          disabled={ro || !novoPagValor.trim()}
                        >
                          <FiPlus aria-hidden /> Adicionar
                        </button>
                      </div>
                      {novoPagError ? (
                        <span className="fin-field-msg fin-field-msg--error">{novoPagError}</span>
                      ) : null}
                      {saldoRestante > 0 && pagamentos.length > 0 ? (
                        <button
                          type="button"
                          className="fin-btn fin-btn--link fin-pag-quitar-btn"
                          onClick={quitarSaldoAgora}
                          title={`Adicionar pagamento de ${formatBRL(saldoRestante)} (hoje)`}
                        >
                          Quitar saldo restante ({formatBRL(saldoRestante)})
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </section>

          {renderDocumentoComprovante()}
        </>
      ) : null}

      </fieldset>

      {!ro ? (
      <div className="fin-form__actions">
        {isEditing ? (
          <button type="button" className="fin-btn fin-btn--ghost" onClick={onCancelar} disabled={saving}>
            Cancelar
          </button>
        ) : null}
        <button type="submit" className="fin-btn fin-btn--primary" disabled={saving || listBlocked}>
          {saving ? <FinSpinner /> : null}
          {saving
            ? 'Salvando…'
            : isEditing
              ? 'Salvar alterações'
              : modo === 'lote'
                ? 'Salvar todas as parcelas'
                : 'Salvar lançamento'}
        </button>
      </div>
      ) : null}
    </form>
    <FinConfirmModal
      open={pagamentoRemoverIdx != null}
      title={`Remover ${tipo === 'entrada' ? 'recebimento' : 'pagamento'}?`}
      message={
        pagamentoEmRemocao ? (
          <>
            Esta ação remove o {tipo === 'entrada' ? 'recebimento' : 'pagamento'} de{' '}
            <strong>{formatBRL(pagamentoEmRemocao.valor)}</strong>
            {pagamentoEmRemocao.data
              ? <> de <strong>{new Date(pagamentoEmRemocao.data + 'T12:00:00').toLocaleDateString('pt-BR')}</strong></>
              : null}
            {pagamentoEmRemocao.responsavel
              ? <> ({pagamentoEmRemocao.responsavel})</>
              : null}
            . O total pago será recalculado.
          </>
        ) : null
      }
      confirmLabel="Remover"
      busy={false}
      onCancel={() => setPagamentoRemoverIdx(null)}
      onConfirm={() => {
        if (pagamentoRemoverIdx != null) removePagamento(pagamentoRemoverIdx);
        setPagamentoRemoverIdx(null);
      }}
    />
    <PagamentoEditModal
      open={pagamentoEditarIdx != null}
      pagamento={pagamentoEmEdicao}
      tipo={tipo}
      metodos={metodos}
      saldoDisponivel={
        pagamentoEmEdicao
          ? roundMoney2(saldoRestante + (Number(pagamentoEmEdicao.valor) || 0))
          : 0
      }
      onCancel={() => setPagamentoEditarIdx(null)}
      onSave={(updated) => {
        if (pagamentoEditarIdx != null) editPagamento(pagamentoEditarIdx, updated);
        setPagamentoEditarIdx(null);
      }}
    />
    </>
  );
}

function PagamentoEditModal({
  open,
  pagamento,
  tipo,
  metodos,
  saldoDisponivel,
  onCancel,
  onSave,
}: {
  open: boolean;
  pagamento: Pagamento | null;
  tipo: 'entrada' | 'saida';
  metodos: CadastroItem[];
  /** Saldo restante do lançamento + valor atual deste pagamento (limite máximo permitido). */
  saldoDisponivel: number;
  onCancel: () => void;
  onSave: (updated: Pagamento) => void;
}): React.ReactElement | null {
  const [displayed, setDisplayed] = useState(open);
  const [leaving, setLeaving] = useState(false);
  const [valorInput, setValorInput] = useState('');
  const [dataInput, setDataInput] = useState('');
  const [responsavelInput, setResponsavelInput] = useState('');
  const [metodoInput, setMetodoInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const metEmpty = metodos.length === 0;

  const requestClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  useEffect(() => {
    if (open && pagamento) {
      setLeaving(false);
      setDisplayed(true);
      setValorInput(decimalToBrInput(pagamento.valor));
      setDataInput(pagamento.data ?? '');
      setResponsavelInput(pagamento.responsavel ?? '');
      setMetodoInput(pagamento.metodo ?? '');
      setError(null);
      return;
    }
    if (displayed) {
      setLeaving(true);
      const t = window.setTimeout(() => {
        setDisplayed(false);
        setLeaving(false);
      }, LEAVE_MS);
      return () => window.clearTimeout(t);
    }
  }, [open, pagamento, displayed]);

  useEffect(() => {
    if (!displayed || leaving) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [displayed, leaving, requestClose]);

  if (!displayed || !pagamento) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = parseMoneyInput(valorInput);
    if (v == null || v <= 0) {
      setError('Informe um valor maior que zero.');
      return;
    }
    if (saldoDisponivel > 0 && v > saldoDisponivel + 1e-9) {
      setError(`Excede o saldo disponível (${formatBRL(saldoDisponivel)}).`);
      return;
    }
    const metodoTrim = metodoInput.trim();
    if (!metEmpty && !metodoTrim) {
      setError('Selecione o método de pagamento.');
      return;
    }
    const data = dataInput.trim() || todayLocalIso();
    const responsavel = responsavelInput.trim() || undefined;
    onSave({ ...pagamento, valor: roundMoney2(v), data, responsavel, metodo: metodoTrim || undefined });
  };

  const titulo = tipo === 'entrada' ? 'Editar recebimento' : 'Editar pagamento';

  return ReactDOM.createPortal(
    <div
      role="presentation"
      className={`fin-cad-modal__overlay${leaving ? ' fin-cad-modal__overlay--leave' : ''}`}
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) requestClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`fin-cad-modal__dialog${leaving ? ' fin-cad-modal__dialog--leave' : ''}`}
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="fin-cad-modal__header">
          <h2 className="fin-cad-modal__title" id={titleId}>
            {titulo}
          </h2>
          <button
            type="button"
            className="fin-cad-modal__close"
            onClick={requestClose}
            aria-label="Fechar"
          >
            <FiX aria-hidden />
          </button>
        </div>
        <form className="fin-cad-modal__form" onSubmit={submit}>
          <div className="fin-cad-modal__body">
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-pag-edit-valor">
                Valor (R$) <span className="fin-cad-modal__req">*</span>
              </label>
              <input
                id="fin-pag-edit-valor"
                type="text"
                inputMode="decimal"
                className={`fin-cad-modal__input${error ? ' fin-cad-modal__input--invalid' : ''}`}
                value={valorInput}
                onChange={(e) => {
                  setValorInput(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="0,00"
                autoComplete="off"
              />
              {saldoDisponivel > 0 ? (
                <p className="fin-cad-modal__hint">
                  Saldo disponível para esta entrada: <strong>{formatBRL(saldoDisponivel)}</strong>
                </p>
              ) : null}
            </div>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-pag-edit-data">
                Data
              </label>
              <FinDatePicker
                id="fin-pag-edit-data"
                value={dataInput}
                onChange={(iso) => {
                  setDataInput(iso);
                  if (error) setError(null);
                }}
                placeholder="Hoje"
              />
            </div>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-pag-edit-met">
                Método de pagamento
                {!metEmpty ? <span className="fin-cad-modal__req">*</span> : null}
              </label>
              <select
                id="fin-pag-edit-met"
                className={`fin-cad-modal__input${error && !metodoInput.trim() && !metEmpty ? ' fin-cad-modal__input--invalid' : ''}`}
                value={metodoInput}
                onChange={(e) => {
                  setMetodoInput(e.target.value);
                  if (error) setError(null);
                }}
                disabled={metEmpty}
              >
                <option value="">Selecione…</option>
                {metodos.map((m) => (
                  <option key={m.id} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-pag-edit-resp">
                Responsável
              </label>
              <input
                id="fin-pag-edit-resp"
                type="text"
                className="fin-cad-modal__input"
                value={responsavelInput}
                onChange={(e) => setResponsavelInput(e.target.value)}
                placeholder="Opcional"
                autoComplete="organization"
              />
            </div>
            {error ? (
              <p className="fin-cad-modal__hint fin-cad-modal__hint--error">{error}</p>
            ) : null}
          </div>
          <div className="fin-cad-modal__footer">
            <button
              type="button"
              className="fin-cad-modal__btn fin-cad-modal__btn--ghost"
              onClick={requestClose}
            >
              Cancelar
            </button>
            <button type="submit" className="fin-cad-modal__btn fin-cad-modal__btn--primary">
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export function FinanceiroCadastrosPage(): React.ReactElement {
  const data = useFinanceiroData();
  const { permissions } = data;
  const [aba, setAba] = useState<'todos' | 'entrada' | 'saida'>('todos');

  const showClientes = aba === 'entrada' || aba === 'todos';
  const showPlanoEntrada = aba === 'entrada' || aba === 'todos';
  const showPlanoSaida = aba === 'saida' || aba === 'todos';

  if (data.cadastrosLoading) {
    return (
      <PageShell title="Cadastros" subtitle="Carregando…">
        <FinCard title="Aguarde">
          <p className="fin-card__hint">Buscando cadastros no servidor.</p>
        </FinCard>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Cadastros"
      subtitle="Use as abas para ver só entradas, só saídas ou tudo junto. Categoria é única; planos de contas seguem o tipo."
    >
      <div className="fin-cadastros-toolbar">
        <div className="fin-segmented" role="tablist" aria-label="Escopo dos cadastros">
          {(['todos', 'entrada', 'saida'] as const).map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={aba === f}
              className={`fin-segmented__btn${aba === f ? ' fin-segmented__btn--active' : ''}`}
              onClick={() => setAba(f)}
            >
              {f === 'todos' ? 'Todos' : f === 'entrada' ? 'Entradas' : 'Saídas'}
            </button>
          ))}
        </div>
      </div>
      <div className="fin-cadastros-layout">
        {showClientes ? (
          <CadastroTabela
            titulo="Clientes"
            linhas={data.clientes}
            saving={data.cadastroSaving}
            onAdicionar={data.addCliente}
            onRemover={data.removeCliente}
            onAtualizar={data.updateCadastroNome}
            placeholderNovo="Nome do cliente"
            canCreate={permissions.canCreate}
            canEdit={permissions.canEdit}
            canDelete={permissions.canDelete}
            searchable
            searchPlaceholder="Buscar cliente pelo nome…"
          />
        ) : null}
        <CadastroTabela
          titulo="Categoria"
          linhas={data.categorias}
          saving={data.cadastroSaving}
          onAdicionar={data.addCategoria}
          onRemover={data.removeCategoria}
          onAtualizar={data.updateCadastroNome}
          placeholderNovo="Nome — uso em entradas e saídas"
          canCreate={permissions.canCreate}
          canEdit={permissions.canEdit}
          canDelete={permissions.canDelete}
        />
        {showPlanoEntrada ? (
          <CadastroTabela
            titulo="Plano de contas — entradas"
            linhas={data.planosDeContasEntrada}
            saving={data.cadastroSaving}
            onAdicionar={data.addPlanoDeContasEntrada}
            onRemover={data.removePlanoDeContasEntrada}
            onAtualizar={data.updateCadastroNome}
            placeholderNovo="Conta (uso em entradas)"
            canCreate={permissions.canCreate}
            canEdit={permissions.canEdit}
            canDelete={permissions.canDelete}
          />
        ) : null}
        {showPlanoSaida ? (
          <CadastroTabela
            titulo="Plano de contas — saídas"
            linhas={data.planosDeContasSaida}
            saving={data.cadastroSaving}
            onAdicionar={data.addPlanoDeContasSaida}
            onRemover={data.removePlanoDeContasSaida}
            onAtualizar={data.updateCadastroNome}
            placeholderNovo="Conta (uso em saídas)"
            canCreate={permissions.canCreate}
            canEdit={permissions.canEdit}
            canDelete={permissions.canDelete}
          />
        ) : null}
        <CadastroTabela
          titulo="Método de pagamento"
          linhas={data.metodos}
          saving={data.cadastroSaving}
          onAdicionar={data.addMetodo}
          onRemover={data.removeMetodo}
          onAtualizar={data.updateCadastroNome}
          placeholderNovo="Ex.: Pix, cartão, boleto"
          canCreate={permissions.canCreate}
          canEdit={permissions.canEdit}
          canDelete={permissions.canDelete}
        />
      </div>
    </PageShell>
  );
}

function ExportRecebimentosModal({
  open,
  onRequestClose,
  lancamentos,
  defaultStart,
  defaultEnd,
}: {
  open: boolean;
  onRequestClose: () => void;
  lancamentos: Lancamento[];
  defaultStart: string;
  defaultEnd: string;
}): React.ReactElement | null {
  const [displayed, setDisplayed] = useState(open);
  const [leaving, setLeaving] = useState(false);
  const [dataInicio, setDataInicio] = useState(defaultStart);
  const [dataFim, setDataFim] = useState(defaultEnd);
  const [tipo, setTipo] = useState<ExportLancamentosTipoFiltro>('todas');
  const [situacao, setSituacao] = useState<ExportLancamentosSituacaoFiltro>('todas');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [exporting, setExporting] = useState(false);

  const requestClose = useCallback(() => {
    if (exporting) return;
    setSubmitAttempted(false);
    onRequestClose();
  }, [exporting, onRequestClose]);

  useEffect(() => {
    if (open) {
      setLeaving(false);
      setDisplayed(true);
      setDataInicio(defaultStart);
      setDataFim(defaultEnd);
      setTipo('todas');
      setSituacao('todas');
      setSubmitAttempted(false);
      return;
    }
    setSubmitAttempted(false);
    if (displayed) {
      setLeaving(true);
      const t = window.setTimeout(() => {
        setDisplayed(false);
        setLeaving(false);
      }, LEAVE_MS);
      return () => window.clearTimeout(t);
    }
  }, [open, displayed, defaultStart, defaultEnd]);

  useEffect(() => {
    if (!displayed || leaving) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !exporting) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [displayed, leaving, exporting, requestClose]);

  const isoOk = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
  const rangeError =
    submitAttempted &&
    (!isoOk(dataInicio) || !isoOk(dataFim)
      ? 'Informe data inicial e final válidas.'
      : dataInicio > dataFim
        ? 'A data inicial não pode ser posterior à data final.'
        : '');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (exporting) return;
    setSubmitAttempted(true);
    if (!isoOk(dataInicio) || !isoOk(dataFim) || dataInicio > dataFim) return;
    const rows = pickLancamentosParaExport(lancamentos, dataInicio, dataFim, tipo, situacao);
    setExporting(true);
    void downloadLancamentosExcelStyled(rows, { start: dataInicio, end: dataFim }, situacao)
      .then(() => {
        requestClose();
      })
      .catch(() => {})
      .finally(() => setExporting(false));
  };

  if (!displayed) return null;

  return ReactDOM.createPortal(
    <div
      role="presentation"
      className={`fin-cad-modal__overlay${leaving ? ' fin-cad-modal__overlay--leave' : ''}`}
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) requestClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="fin-export-rec-title"
        className={`fin-cad-modal__dialog fin-export-modal__dialog${leaving ? ' fin-cad-modal__dialog--leave' : ''}`}
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="fin-cad-modal__header">
          <h2 className="fin-cad-modal__title" id="fin-export-rec-title">
            Exportar lançamentos
          </h2>
          <button
            type="button"
            className="fin-cad-modal__close"
            onClick={requestClose}
            disabled={exporting}
            aria-label="Fechar"
          >
            <FiX />
          </button>
        </div>
        <form className="fin-cad-modal__form" onSubmit={submit}>
          <div className="fin-cad-modal__body fin-export-modal__body">
            <div className="fin-export-modal__dates">
              <div className="fin-cad-modal__field">
                <label className="fin-cad-modal__label" htmlFor="fin-export-dt-ini">
                  Data inicial <span className="fin-cad-modal__req">*</span>
                </label>
                <FinDatePicker
                  id="fin-export-dt-ini"
                  value={dataInicio}
                  onChange={setDataInicio}
                  placeholder="Início do período"
                  hasError={Boolean(rangeError)}
                  onTriggerBlur={() => setSubmitAttempted(true)}
                />
              </div>
              <div className="fin-cad-modal__field">
                <label className="fin-cad-modal__label" htmlFor="fin-export-dt-fim">
                  Data final <span className="fin-cad-modal__req">*</span>
                </label>
                <FinDatePicker
                  id="fin-export-dt-fim"
                  value={dataFim}
                  onChange={setDataFim}
                  placeholder="Fim do período"
                  hasError={Boolean(rangeError)}
                  onTriggerBlur={() => setSubmitAttempted(true)}
                />
              </div>
            </div>
            {rangeError ? (
              <p className="fin-cad-modal__hint fin-cad-modal__hint--error fin-export-modal__range-err">{rangeError}</p>
            ) : null}
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-export-tipo">
                Tipo
              </label>
              <select
                id="fin-export-tipo"
                className="fin-cad-modal__select"
                value={tipo}
                onChange={(ev) => setTipo(ev.target.value as ExportLancamentosTipoFiltro)}
                disabled={exporting}
              >
                <option value="todas">Todos</option>
                <option value="entrada">Entradas</option>
                <option value="saida">Saídas</option>
              </select>
            </div>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor="fin-export-sit">
                Situação
              </label>
              <select
                id="fin-export-sit"
                className="fin-cad-modal__select"
                value={situacao}
                onChange={(ev) => setSituacao(ev.target.value as ExportLancamentosSituacaoFiltro)}
                disabled={exporting}
              >
                <option value="todas">Todas</option>
                <option value="pendente">Pendente</option>
                <option value="pago">Pago</option>
                <option value="atrasado">Em atraso</option>
              </select>
            </div>
          </div>
          <div className="fin-cad-modal__footer">
            <button
              type="button"
              className="fin-cad-modal__btn fin-cad-modal__btn--ghost"
              onClick={requestClose}
              disabled={exporting}
            >
              Cancelar
            </button>
            <button type="submit" className="fin-cad-modal__btn fin-cad-modal__btn--primary" disabled={exporting}>
              {exporting ? (
                <>
                  <FinSpinner />
                  Gerando…
                </>
              ) : (
                <>
                  <FiDownload className="fin-export-modal__btn-icon" aria-hidden />
                  Gerar Excel
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function CadastroAddModal({
  open,
  onRequestClose,
  titulo,
  placeholderNovo,
  saving,
  onAdicionar,
}: {
  open: boolean;
  onRequestClose: () => void;
  titulo: string;
  placeholderNovo: string;
  saving: boolean;
  onAdicionar: (name: string) => Promise<void>;
}): React.ReactElement | null {
  const [displayed, setDisplayed] = useState(open);
  const [leaving, setLeaving] = useState(false);
  const [novo, setNovo] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = novo.trim();
  const emptyError = submitAttempted && !trimmed ? 'Informe o nome para cadastrar.' : '';
  const idSafe = titulo.replace(/\s/g, '-');

  const requestClose = useCallback(() => {
    if (saving) return;
    setSubmitAttempted(false);
    onRequestClose();
  }, [saving, onRequestClose]);

  useEffect(() => {
    if (open) {
      setLeaving(false);
      setDisplayed(true);
      setNovo('');
      setSubmitAttempted(false);
      return;
    }
    setSubmitAttempted(false);
    if (displayed) {
      setLeaving(true);
      const t = window.setTimeout(() => {
        setDisplayed(false);
        setLeaving(false);
      }, LEAVE_MS);
      return () => window.clearTimeout(t);
    }
  }, [open, displayed]);

  useEffect(() => {
    if (!open || leaving || !displayed) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open, leaving, displayed]);

  useEffect(() => {
    if (!displayed || leaving) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [displayed, leaving, saving, requestClose]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSubmitAttempted(true);
    if (!trimmed) return;
    void onAdicionar(trimmed)
      .then(() => {
        requestClose();
      })
      .catch(() => {});
  };

  if (!displayed) return null;

  return ReactDOM.createPortal(
    <div
      role="presentation"
      className={`fin-cad-modal__overlay${leaving ? ' fin-cad-modal__overlay--leave' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`fin-cadastro-modal-title-${idSafe}`}
        className={`fin-cad-modal__dialog${leaving ? ' fin-cad-modal__dialog--leave' : ''}`}
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="fin-cad-modal__header">
          <h2 className="fin-cad-modal__title" id={`fin-cadastro-modal-title-${idSafe}`}>
            Novo cadastro
          </h2>
          <button
            type="button"
            className="fin-cad-modal__close"
            onClick={requestClose}
            disabled={saving}
            aria-label="Fechar"
          >
            <FiX />
          </button>
        </div>
        <form className="fin-cad-modal__form" onSubmit={submit}>
          <div className="fin-cad-modal__body">
            <p className="fin-cad-modal__context">{titulo}</p>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor={`fin-cadastro-input-${idSafe}`}>
                Nome <span className="fin-cad-modal__req">*</span>
              </label>
              <input
                ref={inputRef}
                id={`fin-cadastro-input-${idSafe}`}
                type="text"
                className={`fin-cad-modal__input${emptyError ? ' fin-cad-modal__input--invalid' : ''}`}
                value={novo}
                onChange={(ev) => setNovo(ev.target.value)}
                placeholder={placeholderNovo}
                disabled={saving}
                autoComplete="off"
              />
              {emptyError ? (
                <p className="fin-cad-modal__hint fin-cad-modal__hint--error">{emptyError}</p>
              ) : null}
            </div>
          </div>
          <div className="fin-cad-modal__footer">
            <button
              type="button"
              className="fin-cad-modal__btn fin-cad-modal__btn--ghost"
              onClick={requestClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button type="submit" className="fin-cad-modal__btn fin-cad-modal__btn--primary" disabled={saving}>
              {saving ? <FinSpinner /> : null}
              {saving ? 'Salvando…' : 'Adicionar'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function CadastroEditModal({
  item,
  onRequestClose,
  titulo,
  placeholderNovo,
  saving,
  onAtualizar,
}: {
  item: CadastroItem | null;
  onRequestClose: () => void;
  titulo: string;
  placeholderNovo: string;
  saving: boolean;
  onAtualizar: (id: number, name: string) => Promise<void>;
}): React.ReactElement | null {
  const [nome, setNome] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const idSafe = titulo.replace(/\s/g, '-');

  const requestClose = useCallback(() => {
    if (saving) return;
    setSubmitAttempted(false);
    onRequestClose();
  }, [saving, onRequestClose]);

  useEffect(() => {
    if (!item) {
      setSubmitAttempted(false);
      return;
    }
    setNome(item.name);
    setSubmitAttempted(false);
  }, [item]);

  useEffect(() => {
    if (!item) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [item]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, saving, requestClose]);

  if (!item) return null;

  const trimmed = nome.trim();
  const emptyError = submitAttempted && !trimmed ? 'Informe o nome.' : '';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSubmitAttempted(true);
    if (!trimmed) return;
    void onAtualizar(item.id, trimmed)
      .then(() => requestClose())
      .catch(() => {});
  };

  return ReactDOM.createPortal(
    <div
      role="presentation"
      className="fin-cad-modal__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`fin-cadastro-edit-title-${idSafe}-${item.id}`}
        className="fin-cad-modal__dialog"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="fin-cad-modal__header">
          <h2 className="fin-cad-modal__title" id={`fin-cadastro-edit-title-${idSafe}-${item.id}`}>
            Editar cadastro
          </h2>
          <button
            type="button"
            className="fin-cad-modal__close"
            onClick={requestClose}
            disabled={saving}
            aria-label="Fechar"
          >
            <FiX />
          </button>
        </div>
        <form className="fin-cad-modal__form" onSubmit={submit}>
          <div className="fin-cad-modal__body">
            <p className="fin-cad-modal__context">{titulo}</p>
            <div className="fin-cad-modal__field">
              <label className="fin-cad-modal__label" htmlFor={`fin-cadastro-edit-input-${idSafe}-${item.id}`}>
                Nome <span className="fin-cad-modal__req">*</span>
              </label>
              <input
                ref={inputRef}
                id={`fin-cadastro-edit-input-${idSafe}-${item.id}`}
                type="text"
                className={`fin-cad-modal__input${emptyError ? ' fin-cad-modal__input--invalid' : ''}`}
                value={nome}
                onChange={(ev) => setNome(ev.target.value)}
                placeholder={placeholderNovo}
                disabled={saving}
                autoComplete="off"
              />
              {emptyError ? (
                <p className="fin-cad-modal__hint fin-cad-modal__hint--error">{emptyError}</p>
              ) : null}
            </div>
          </div>
          <div className="fin-cad-modal__footer">
            <button
              type="button"
              className="fin-cad-modal__btn fin-cad-modal__btn--ghost"
              onClick={requestClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button type="submit" className="fin-cad-modal__btn fin-cad-modal__btn--primary" disabled={saving}>
              {saving ? <FinSpinner /> : null}
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function FinConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Excluir',
  cancelLabel = 'Cancelar',
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}): React.ReactElement | null {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    void onConfirm();
  };

  return ReactDOM.createPortal(
    <div
      role="presentation"
      className="fin-cad-modal__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fin-cad-modal__dialog fin-confirm-modal__dialog"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="fin-cad-modal__header">
          <h2 className="fin-cad-modal__title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="fin-cad-modal__close"
            onClick={onCancel}
            disabled={busy}
            aria-label="Fechar"
          >
            <FiX />
          </button>
        </div>
        <form className="fin-cad-modal__form" onSubmit={submit}>
          <div className="fin-cad-modal__body">
            <div className="fin-confirm-modal__message">{message}</div>
          </div>
          <div className="fin-cad-modal__footer">
            <button
              type="button"
              className="fin-cad-modal__btn fin-cad-modal__btn--ghost"
              onClick={onCancel}
              disabled={busy}
            >
              {cancelLabel}
            </button>
            <button type="submit" className="fin-cad-modal__btn fin-cad-modal__btn--danger" disabled={busy}>
              {busy ? <FinSpinner /> : null}
              {busy ? 'Excluindo…' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

const CADASTRO_PAGE_SIZE = 5;

/** Paginação dos painéis de cadastro (clientes, 4 painéis entrada/saída, método de pagamento). */
function CadastroPagination({
  page,
  totalItems,
  pageSize,
  onPageChange,
  disabled,
}: {
  page: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  disabled?: boolean;
}): React.ReactElement {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  const go = (p: number) => {
    if (disabled) return;
    const next = Math.max(1, Math.min(totalPages, p));
    if (next !== page) onPageChange(next);
  };

  const pagesToShow = (): number[] => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const windowSize = 5;
    let start = Math.max(1, page - 2);
    let end = start + windowSize - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - windowSize + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const pageNums = pagesToShow();

  return (
    <nav className="fin-cad-pager" aria-label="Paginação da lista de cadastros">
      <p className="fin-cad-pager__meta">
        {totalItems === 0 ? (
          <>Nenhum item na lista</>
        ) : (
          <>
            <span className="fin-cad-pager__meta-lead">Mostrando</span>
            <span className="fin-cad-pager__range">
              {from}–{to}
            </span>
            <span className="fin-cad-pager__meta-mid">de</span>
            <span className="fin-cad-pager__total">{totalItems}</span>
          </>
        )}
      </p>
      <div className="fin-cad-pager__controls">
        <button
          type="button"
          className="fin-cad-pager__btn"
          disabled={disabled || page <= 1}
          onClick={() => go(page - 1)}
          aria-label="Página anterior"
        >
          <FiChevronLeft aria-hidden />
        </button>
        <div className="fin-cad-pager__pages">
          {pageNums.map((p) => (
            <button
              key={p}
              type="button"
              className={`fin-cad-pager__page${p === page ? ' fin-cad-pager__page--active' : ''}`}
              disabled={disabled}
              onClick={() => go(p)}
              aria-label={`Ir para página ${p}`}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="fin-cad-pager__btn"
          disabled={disabled || page >= totalPages}
          onClick={() => go(page + 1)}
          aria-label="Próxima página"
        >
          <FiChevronRight aria-hidden />
        </button>
      </div>
    </nav>
  );
}

function CadastroTabela({
  titulo,
  hint,
  linhas,
  saving,
  onAdicionar,
  onRemover,
  onAtualizar,
  placeholderNovo,
  canCreate,
  canEdit,
  canDelete,
  searchable = false,
  searchPlaceholder,
}: {
  titulo: string;
  hint?: string;
  linhas: CadastroItem[];
  saving: boolean;
  onAdicionar: (name: string) => Promise<void>;
  onRemover: (id: number) => Promise<void>;
  onAtualizar: (id: number, name: string) => Promise<void>;
  placeholderNovo: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
}): React.ReactElement {
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<CadastroItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<CadastroItem | null>(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const showActionsCol = canDelete || canEdit;

  const linhasFiltradas = useMemo(() => {
    if (!searchable) return linhas;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return linhas;
    return linhas.filter((l) => l.name.toLowerCase().includes(q));
  }, [linhas, searchable, searchQuery]);

  const offset = (page - 1) * CADASTRO_PAGE_SIZE;
  const linhasPagina = linhasFiltradas.slice(offset, offset + CADASTRO_PAGE_SIZE);
  const showPager = linhasFiltradas.length > CADASTRO_PAGE_SIZE;
  const isFiltering = searchable && searchQuery.trim().length > 0;

  useEffect(() => {
    setPage((p) => {
      const tp = Math.max(1, Math.ceil(linhasFiltradas.length / CADASTRO_PAGE_SIZE));
      if (p > tp) return tp;
      if (p < 1) return 1;
      return p;
    });
  }, [linhasFiltradas.length]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  return (
    <FinCard
      className="fin-card--cadastro"
      title={titulo}
      headerRight={
        <>
          <span className="fin-cadastro-badge" title={`${linhas.length} itens`}>
            {linhas.length}
          </span>
          {canCreate ? (
            <button
              type="button"
              className="fin-cad-add-btn"
              disabled={saving}
              onClick={() => setAddOpen(true)}
              aria-label={`Adicionar em ${titulo}`}
            >
              <FiPlus aria-hidden />
            </button>
          ) : null}
        </>
      }
    >
      {hint ? <p className="fin-card__hint">{hint}</p> : null}
      {canCreate ? (
        <CadastroAddModal
          open={addOpen}
          onRequestClose={() => setAddOpen(false)}
          titulo={titulo}
          placeholderNovo={placeholderNovo}
          saving={saving}
          onAdicionar={onAdicionar}
        />
      ) : null}
      {canEdit ? (
        <CadastroEditModal
          item={editItem}
          onRequestClose={() => setEditItem(null)}
          titulo={titulo}
          placeholderNovo={placeholderNovo}
          saving={saving}
          onAtualizar={onAtualizar}
        />
      ) : null}
      {canDelete ? (
        <FinConfirmModal
          open={deleteItem != null}
          title="Excluir cadastro?"
          message={
            deleteItem ? (
              <>
                Tem certeza que deseja excluir <strong>{deleteItem.name}</strong>? Esta ação não pode ser desfeita.
              </>
            ) : null
          }
          busy={saving}
          onCancel={() => {
            if (!saving) setDeleteItem(null);
          }}
          onConfirm={async () => {
            if (!deleteItem) return;
            try {
              await onRemover(deleteItem.id);
              setDeleteItem(null);
            } catch {
              /* erro exibido no banner da API */
            }
          }}
        />
      ) : null}
      {searchable ? (
        <div className="fin-cad-search">
          <FiSearch className="fin-cad-search__icon" aria-hidden />
          <input
            type="text"
            className="fin-cad-search__input"
            placeholder={searchPlaceholder ?? `Buscar em ${titulo.toLowerCase()}…`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label={`Filtrar ${titulo.toLowerCase()} pelo nome`}
          />
          {searchQuery ? (
            <button
              type="button"
              className="fin-cad-search__clear"
              onClick={() => setSearchQuery('')}
              aria-label="Limpar busca"
            >
              <FiX aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="fin-cadastro-table-wrap">
        <table className="fin-table fin-table--compact">
          <thead>
            <tr>
              <th className="fin-table__col-num">Nº</th>
              <th>Nome</th>
              {showActionsCol ? (
              <th className="fin-table__th-actions" scope="col">
                Ações
              </th>
            ) : null}
            </tr>
          </thead>
          <tbody>
            {linhasFiltradas.length === 0 ? (
              <tr>
                <td colSpan={showActionsCol ? 3 : 2} className="fin-table__empty">
                  {isFiltering
                    ? `Nenhum resultado para "${searchQuery.trim()}".`
                    : canCreate
                      ? 'Nenhum cadastro. Clique em Adicionar para incluir.'
                      : 'Nenhum cadastro.'}
                </td>
              </tr>
            ) : (
              linhasPagina.map((item, idx) => (
                <tr key={item.id}>
                  <td className="fin-table__col-num">{offset + idx + 1}</td>
                  <td>{item.name}</td>
                  {showActionsCol ? (
                    <td className="fin-table__actions">
                      <div className="fin-row-actions">
                        {canEdit ? (
                          <button
                            type="button"
                            className="fin-icon-btn fin-icon-btn--ghost"
                            disabled={saving}
                            onClick={() => setEditItem(item)}
                            aria-label={`Editar ${item.name}`}
                          >
                            <FiEdit2 aria-hidden />
                          </button>
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="fin-icon-btn fin-icon-btn--danger"
                            disabled={saving}
                            onClick={() => setDeleteItem(item)}
                            aria-label={`Excluir ${item.name}`}
                          >
                            <FiTrash2 aria-hidden />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showPager ? (
        <CadastroPagination
          page={page}
          totalItems={linhasFiltradas.length}
          pageSize={CADASTRO_PAGE_SIZE}
          onPageChange={setPage}
          disabled={saving}
        />
      ) : null}
    </FinCard>
  );
}
